/**
 * Clariva — application translations.
 *
 * One file, all 8 supported locales. Keys are namespaced:
 *   nav.*           — sidebar / topnav links
 *   common.*        — buttons, generic words
 *   landing.*       — public marketing site (Landing.jsx)
 *
 * Missing keys fall back to English (`en`) automatically — see I18nContext.t().
 * Native script is used wherever possible; everything machine-translatable
 * has been translated and reviewed for B2B SaaS register.
 */

export const SUPPORTED_LANGS = [
  { code: "en", name: "English",  native: "English",  rtl: false, currency: "USD" },
  { code: "ar", name: "Arabic",   native: "العربية",  rtl: true,  currency: "AED" },
  { code: "fr", name: "French",   native: "Français", rtl: false, currency: "EUR" },
  { code: "es", name: "Spanish",  native: "Español",  rtl: false, currency: "EUR" },
  { code: "de", name: "German",   native: "Deutsch",  rtl: false, currency: "EUR" },
  { code: "ja", name: "Japanese", native: "日本語",     rtl: false, currency: "JPY" },
  { code: "zh", name: "Mandarin", native: "中文",       rtl: false, currency: "CNY" },
  { code: "ko", name: "Korean",   native: "한국어",     rtl: false, currency: "KRW" },
];

// ISO-3166 alpha-2 country → preferred lang & currency. Used for geo-based
// auto-detection. Anything not in this map falls back to en/USD.
export const COUNTRY_DEFAULTS = {
  // English-first markets (USD/GBP/AUD/CAD)
  US: { lang: "en", currency: "USD" }, GB: { lang: "en", currency: "GBP" },
  IE: { lang: "en", currency: "EUR" }, AU: { lang: "en", currency: "AUD" },
  CA: { lang: "en", currency: "CAD" }, NZ: { lang: "en", currency: "NZD" },
  IN: { lang: "en", currency: "INR" }, SG: { lang: "en", currency: "SGD" },
  // Arabic / MENA
  AE: { lang: "ar", currency: "AED" }, SA: { lang: "ar", currency: "SAR" },
  QA: { lang: "ar", currency: "QAR" }, KW: { lang: "ar", currency: "KWD" },
  BH: { lang: "ar", currency: "BHD" }, OM: { lang: "ar", currency: "OMR" },
  EG: { lang: "ar", currency: "EGP" }, JO: { lang: "ar", currency: "JOD" },
  // French
  FR: { lang: "fr", currency: "EUR" }, BE: { lang: "fr", currency: "EUR" },
  LU: { lang: "fr", currency: "EUR" }, CH: { lang: "fr", currency: "CHF" },
  MC: { lang: "fr", currency: "EUR" },
  // Spanish
  ES: { lang: "es", currency: "EUR" }, MX: { lang: "es", currency: "MXN" },
  AR: { lang: "es", currency: "ARS" }, CL: { lang: "es", currency: "CLP" },
  CO: { lang: "es", currency: "COP" }, PE: { lang: "es", currency: "PEN" },
  // German
  DE: { lang: "de", currency: "EUR" }, AT: { lang: "de", currency: "EUR" },
  // Japanese / Mandarin / Korean
  JP: { lang: "ja", currency: "JPY" },
  CN: { lang: "zh", currency: "CNY" }, TW: { lang: "zh", currency: "TWD" }, HK: { lang: "zh", currency: "HKD" },
  KR: { lang: "ko", currency: "KRW" },
};

/** Translation table — all keys present in every language for clarity. */
const T = {
  /* ============================ ENGLISH ============================ */
  en: {
    "nav.dashboard": "Dashboard", "nav.studio": "Contract Studio", "nav.repository": "Repository",
    "nav.workflows": "Workflows", "nav.impact": "Business Impact", "nav.monitoring": "Monitoring",
    "nav.counterparty": "Counterparty 360°", "nav.integrations": "Integrations",
    "nav.analytics": "Analytics", "nav.team": "Team & Roles", "nav.subscription": "Subscription",
    "nav.compliance": "Compliance", "nav.settings": "Settings",
    "common.save": "Save", "common.cancel": "Cancel", "common.new": "New",
    "common.ask": "Ask Clariva…", "common.signin": "Sign in", "common.getstarted": "Get Started",
    "common.bookdemo": "Book a live demo", "common.startfree": "Start free — no card",
    "common.watch": "Watch 2-min walkthrough", "common.theme": "Theme",
    // Landing — Nav
    "landing.nav.features": "Features", "landing.nav.howitworks": "How it works",
    "landing.nav.industries": "Industries", "landing.nav.mobile": "Mobile app",
        "landing.nav.product": "Product",
    "landing.nav.solutions": "Solutions",
    "landing.nav.testdemo": "Test the demo",
    "landing.nav.pricing": "Pricing",
    "landing.nav.who": "Who we are", "landing.nav.contact": "Contact",
    // Landing — Hero
    "landing.hero.eyebrow": "The Transparent Contract Intelligence Platform",
    "landing.hero.title": "See both sides",
    "landing.hero.subtitle": "Clariva is the first AI contract platform that serves drafters and signatories with equal intelligence — clause-by-clause analysis, audio briefings, pre-signature business impact reports, and drag-and-drop approval workflows.",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10 languages · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "Trusted by fast-moving legal & ops teams",
    // Landing — Features section
    "landing.features.eyebrow": "Built for non-lawyers. Loved by legal.",
    "landing.features.title": "Nine reasons teams replace their redline PDFs with Clariva.",
    "landing.feat.signatory.title": "Signatory Experience",
    "landing.feat.signatory.desc": "For/against clause breakdown from YOUR perspective — not just the drafter's. Finally, transparency at signing.",
    "landing.feat.audio.title": "Audio briefings",
    "landing.feat.audio.desc": "OpenAI TTS reads a 60-second plain-language summary. Perfect for CEOs who don't read contracts.",
    "landing.feat.impact.title": "Business Impact Reports",
    "landing.feat.impact.desc": "Quantified 8-domain pre-signature scoring: financial, operational, reputational, regulatory & more.",
    "landing.feat.workflows.title": "Drag-and-drop Workflows",
    "landing.feat.workflows.desc": "Visual approval designer with fork/join, decision branches, auto-triggers, e-signature & AI steps.",
    "landing.feat.obligations.title": "Obligation Monitoring",
    "landing.feat.obligations.desc": "Track SLAs, deadlines & breach risk. Ripple analysis across your entire portfolio.",
    "landing.feat.compliance.title": "Compliance Vault",
    "landing.feat.compliance.desc": "15 battle-tested templates: HIPAA BAA, GDPR DPA, SaaS MSA, Hospital Consent, Influencer, Music Dist & more.",
    "landing.feat.ai.title": "Claude Sonnet 4.5 analysis",
    "landing.feat.ai.desc": "Frontier AI translates legalese into decisions your CFO can sign off. No hallucinations, with citations.",
    "landing.feat.global.title": "Global-ready",
    "landing.feat.global.desc": "10 languages, RTL Arabic, multi-currency, MEA + EU + APAC data residency from day one.",
    "landing.feat.counterparty.title": "Counterparty 360°",
    "landing.feat.counterparty.desc": "Relationship graph, past redlines, negotiation history. Know who you're signing with.",
    // Landing — How it works
    "landing.how.eyebrow": "From paste to signed in four steps",
    "landing.how.title": "How Clariva turns a draft into a signed deal.",
    "landing.how.s1.title": "Paste or upload",
    "landing.how.s1.desc": "PDF, DOCX, plain text, even a Notion link — Clariva ingests it.",
    "landing.how.s2.title": "AI analyses both sides",
    "landing.how.s2.desc": "Claude reads every clause from drafter AND signatory perspectives.",
    "landing.how.s3.title": "Route for approval",
    "landing.how.s3.desc": "Drag-and-drop the approval chain — Legal → Finance → Signature.",
    "landing.how.s4.title": "Sign with confidence",
    "landing.how.s4.desc": "Counter-parties get the for/against view + audio briefing. No surprises.",
    // Landing — Six-step process
    "landing.process.eyebrow": "The Clariva process",
    "landing.process.title.l1": "From draft to signed,",
    "landing.process.title.l2": "in six transparent stages.",
    "landing.process.subtitle": "Every stage is instrumented. Every AI decision is explained. Every signatory sees what you see.",
    "landing.process.s1.title": "Upload or draft",
    "landing.process.s1.desc": "PDF, DOCX, or start from one of 15 industry templates. OCR-ready.",
    "landing.process.s2.title": "AI dissects every clause",
    "landing.process.s2.desc": "Claude Sonnet 4.5 scores each clause for risk, maps it to obligations, translates legalese.",
    "landing.process.s3.title": "Business Impact Report",
    "landing.process.s3.desc": "8-domain scoring card: financial, operational, reputational & more — delivered in seconds.",
    "landing.process.s4.title": "Audio briefing",
    "landing.process.s4.desc": "60-second OpenAI TTS voice summary. Shareable link for executives & counterparties.",
    "landing.process.s5.title": "Route via workflow",
    "landing.process.s5.desc": "Drag-and-drop designer: approvals, decision branches, fork/join parallel reviews, auto-triggers.",
    "landing.process.s6.title": "Sign with audit trail",
    "landing.process.s6.desc": "QR-shareable e-signature, tamper-proof log, obligation monitor armed from the moment ink dries.",
    // Landing — Walkthrough
    "landing.walkthrough.eyebrow": "2-minute walkthrough",
    "landing.walkthrough.title": "See Clariva dissect a real MSA.",
    "landing.walkthrough.subtitle": "Watch a full contract go from drop-zone to signed in under two minutes — with every AI decision explained.",
    "landing.walkthrough.duration": "2:14 · With voice-over",
    "landing.walkthrough.kpi1.n": "2.1×",
    "landing.walkthrough.kpi1.l": "Faster negotiation cycles",
    "landing.walkthrough.kpi2.n": "68%",
    "landing.walkthrough.kpi2.l": "Fewer missed obligations",
    "landing.walkthrough.kpi3.l": "Avg. first-year savings",
    // Landing — Industries
    "landing.industries.eyebrow": "Industries served",
    "landing.industries.title": "Specialized playbooks for the industries that sign the most paper.",
    "landing.ind.healthcare": "Healthcare", "landing.ind.healthcare.desc": "HIPAA BAA, patient surgery consent, clinical services.",
    "landing.ind.clinical": "Clinical Trials & Pharma Research", "landing.ind.clinical.desc": "Phase I–IV investigator agreements, test-patient informed consent & enrolment, CRO contracts, IRB-ready protocols and adverse-event disclosure clauses.",
    "landing.ind.insurance": "Insurance Companies", "landing.ind.insurance.desc": "Proposals, policy schedules, endorsements — with utmost-good-faith disclosure scanning and claims-clause extraction.",
    "landing.ind.banking": "Banking & Lending", "landing.ind.banking.desc": "Personal loans, home mortgages, business credit — with automated EMI & covenant extraction.",
    "landing.ind.realestate": "Real Estate & Development", "landing.ind.realestate.desc": "Off-plan sales, cash property purchases, escrow & RERA-ready agreements.",
    "landing.ind.leasing": "Leasing & Tenancy", "landing.ind.leasing.desc": "Residential + commercial leases with CPI escalation and rent-review playbooks.",
    "landing.ind.property": "Property Management", "landing.ind.property.desc": "Owner-manager agreements, trust accounting, and vacancy-lifecycle workflows.",
    "landing.ind.financial": "Financial Services", "landing.ind.financial.desc": "MSA, NDA, fintech partnerships, regulatory compliance.",
    "landing.ind.saas": "Enterprise SaaS", "landing.ind.saas.desc": "SaaS MSA, DPA, security addenda, SOC 2 attestations.",
    "landing.ind.legal": "Legal & Counsel", "landing.ind.legal.desc": "Engagement letters, outside counsel, conflict checks.",
    "landing.ind.education": "Education", "landing.ind.education.desc": "Licensing, vendor agreements, research data sharing.",
    // Landing — Mobile app
    "landing.mobile.eyebrow": "Install on mobile",
    "landing.mobile.title.l1": "Approve from your phone.",
    "landing.mobile.title.l2": "Offline-ready. Native-feeling.",
    "landing.mobile.desc": "Clariva is a Progressive Web App — add it to your home screen on iOS or Android in one tap. Your repository caches for offline review, and push notifications ping you the instant a contract is waiting on your approval.",
    "landing.mobile.feat.install.title": "One-tap install",
    "landing.mobile.feat.install.desc": "Home screen icon, no app store",
    "landing.mobile.feat.offline.title": "Offline-ready",
    "landing.mobile.feat.offline.desc": "Read-mode works without signal",
    "landing.mobile.feat.push.title": "Push alerts",
    "landing.mobile.feat.push.desc": "Approvals, signatures, expiries",
    "landing.mobile.qr.title": "Scan to open",
    "landing.mobile.qr.subtitle": "Point your phone camera at a QR code",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → Share → Add to Home",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → menu → Install app",
    "landing.mobile.qr.fallback": "Or just visit",
    // Landing — Testimonials
    "landing.testimonials.eyebrow": "What our users say",
    "landing.testimonials.title": "“We replaced two paralegals and an e-sign tool.”",
    // Landing — Who we are
    "landing.who.eyebrow": "Who we are",
    "landing.who.title": "A team of contract obsessives, ex-legal-tech founders, and AI engineers.",
    "landing.who.desc": "Clariva was founded in Dubai with one belief: signatories deserve the same intelligence drafters have. We're building the contract platform we wished existed when we ran legal at a Series-D fintech.",
    // Landing — Contact
    "landing.contact.eyebrow": "Talk to us",
    "landing.contact.title": "Book a 30-minute live demo.",
    "landing.contact.desc": "Show us your trickiest contract and we'll run it through Clariva live. No slides, no recording — just the product.",
    "landing.contact.form.name": "Full name", "landing.contact.form.email": "Work email",
    "landing.contact.form.company": "Company", "landing.contact.form.message": "What would you like to see?",
    "landing.contact.form.submit": "Request demo",
    // Landing — Final CTA + Footer
    "landing.final.title": "See your next contract from both sides.",
    "landing.final.desc": "Free workspace, three sample contracts, no credit card.",
    "landing.footer.tagline": "The transparent contract intelligence platform.",
    "landing.footer.product": "Product", "landing.footer.company": "Company",
    "landing.footer.legal": "Legal", "landing.footer.rights": "All rights reserved.",
    // Pricing
    "landing.pricing.eyebrow": "Plans for every team",
    "landing.pricing.title": "Simple, transparent pricing.",
    "landing.pricing.subtitle": "Start free with all 9 modules. Upgrade when you outgrow the limits.",
    "landing.pricing.popular": "Most popular",
    "landing.pricing.free": "Free", "landing.pricing.starter": "Starter",
    "landing.pricing.pro": "Professional", "landing.pricing.enterprise": "Enterprise",
    "landing.pricing.cta.free": "Start free",
    "landing.pricing.cta.paid": "Choose plan",
    "landing.pricing.cta.enterprise": "Talk to sales",
    "landing.pricing.contact": "Custom",
    "landing.pricing.billed.monthly": "per user / month",
    "landing.pricing.billed.forever": "forever, no card",
    "landing.pricing.billed.contact": "tailored to your team",
    "landing.pricing.unlimited": "Unlimited",
    "landing.pricing.contracts": "contracts",
    "landing.pricing.seats": "team seats",
    "landing.pricing.fineprint": "Prices shown in {currency}. Currency converted from USD at indicative rates — billed in your contracted currency.",
    // Locale auto-detect banner
    
    "landing.testimonials.t1.quote": "Our GC went from reading 40 contracts a week to reviewing Clariva's impact reports in 10 minutes each. We shipped 3× more deals last quarter.",
    "landing.testimonials.t1.name": "Maya Chen",
    "landing.testimonials.t1.role": "COO, CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "The audio briefing feature alone pays for the whole platform. My CEO actually listens to them during her commute.",
    "landing.testimonials.t2.name": "Luis Rivera",
    "landing.testimonials.t2.role": "VP Legal Ops, Harmonic Wave Records",
    "landing.testimonials.t3.quote": "Finally a tool that doesn't treat signatories like afterthoughts. Our vendors trust us more because they get transparent risk breakdowns before signing.",
    "landing.testimonials.t3.name": "Priya Rahman",
    "landing.testimonials.t3.role": "Founder, Arcadia Health Collective",
    "landing.who.title2": "A team of legal operators, ML researchers, and designers — united by one belief.",
    "landing.who.desc2": "Contracts shouldn't be one-sided weapons. They should be shared understanding between two parties, both equipped with the same clarity. Clariva was born inside a law firm frustrated with watching clients sign things they didn't understand. We're here to make that the past.",
    "landing.who.pillar1.title": "Transparency first",
    "landing.who.pillar1.desc": "We refuse to hide AI reasoning. Every score, every risk flag comes with a citation from the source document.",
    "landing.who.pillar2.title": "Signatories matter",
    "landing.who.pillar2.desc": "Contracts bind both sides. We build for the human who signs last, not just the lawyer who drafts first.",
    "landing.who.pillar3.title": "Enterprise-grade speed",
    "landing.who.pillar3.desc": "Sub-second clause analysis, streaming audio, zero-downtime deployments. Built on Claude 4.5 + FastAPI + React.",
    "landing.contact.eyebrow2": "Let's talk",
    "landing.contact.title2": "Book a full demo, or arrange an eSession.",
    "landing.contact.desc2": "Tell us a little about your team — we'll match you with a specialist who's worked in your industry and put together a personalized walkthrough using your actual contract (under NDA, of course).",
    "landing.contact.bullet1": "Personalized 30-min product walkthrough",
    "landing.contact.bullet2": "Live clause analysis on your real contract",
    "landing.contact.bullet3": "Implementation & rollout playbook",
    "landing.contact.bullet4": "Custom ROI model for your legal spend",
    "landing.contact.intent.demo": "Full demo",
    "landing.contact.intent.esession": "Arrange an eSession",
    "landing.contact.intent.sales": "Contact sales",
    "landing.contact.placeholder.name": "Jane Counselor",
    "landing.contact.placeholder.email": "jane@company.com",
    "landing.contact.placeholder.company": "Company name",
    "landing.contact.placeholder.message": "We're evaluating contract intelligence for our legal ops team...",
    "landing.contact.field.preferred_date": "Preferred date",
    "landing.contact.field.preferred_time": "Preferred time",
    "landing.contact.field.message": "What would you like to achieve?",
    "landing.contact.privacy": "We reply within 2 business hours. No spam, ever.",
    "landing.contact.submit.demo": "Request full demo",
    "landing.contact.submit.esession": "Book eSession",
    "landing.contact.submit.sales": "Contact sales",
    "landing.contact.submitting": "Sending…",
    "landing.contact.success.title": "You're in.",
    "landing.contact.success.desc": "We'll reach out within 2 business hours with a calendar link.",
    "landing.contact.toast.required": "Name and email are required",
    "landing.contact.toast.success": "Thanks! We'll be in touch shortly.",
    "landing.contact.toast.esession": "eSession request received — we'll confirm within 2 business hours",
    "landing.final.title2.l1": "Start seeing both sides",
    "landing.final.title2.l2": "of your next contract.",
    "landing.final.desc2": "Free to start. No credit card. Signup takes under 30 seconds and seeds a full demo workspace for you.",
    "landing.final.cta.start": "Start free now",
    "landing.final.cta.demo": "Try demo@clariva.ai",
    "landing.footer.tagline2": "Transparent AI Contract Intelligence. For drafters and signatories, equally.",
    "landing.footer.product.items": "Features,Workflows,Audio briefings,Business Impact",
    "landing.footer.company.items": "Who we are,Careers,Press kit,Security",
    "landing.footer.legal.items": "Terms,Privacy,DPA,SOC 2 report",
    "landing.footer.copyright": "© 2026 Clariva. Transparent AI Contract Intelligence.",
    "landing.footer.madein": "Made with precision in SF · London · Dubai",
    "locale.banner.detected": "We've set your language to {lang} based on your location.",
    "locale.banner.undo": "Switch back to English",
    "locale.banner.keep": "Keep {lang}",
  },

  /* ============================ ARABIC (RTL) ============================ */
  ar: {
    "nav.dashboard": "لوحة التحكم", "nav.studio": "استوديو العقود", "nav.repository": "المستودع",
    "nav.workflows": "تدفقات الموافقة", "nav.impact": "الأثر التجاري", "nav.monitoring": "المراقبة",
    "nav.counterparty": "الأطراف المقابلة", "nav.integrations": "التكاملات",
    "nav.analytics": "التحليلات", "nav.team": "الفريق والأدوار", "nav.subscription": "الاشتراك",
    "nav.compliance": "الامتثال", "nav.settings": "الإعدادات",
    "common.save": "حفظ", "common.cancel": "إلغاء", "common.new": "جديد",
    "common.ask": "اسأل كلاريفا…", "common.signin": "تسجيل الدخول", "common.getstarted": "ابدأ الآن",
    "common.bookdemo": "احجز عرضًا مباشرًا", "common.startfree": "ابدأ مجانًا — بدون بطاقة",
    "common.watch": "شاهد جولة لمدة دقيقتين", "common.theme": "السمة",
    "landing.nav.features": "الميزات", "landing.nav.howitworks": "كيف يعمل",
    "landing.nav.industries": "القطاعات", "landing.nav.mobile": "تطبيق الجوال",
        "landing.nav.product": "المنتج",
    "landing.nav.solutions": "حلول",
    "landing.nav.testdemo": "جرّب العرض التجريبي",
    "landing.nav.pricing": "الأسعار",
    "landing.nav.who": "من نحن", "landing.nav.contact": "تواصل معنا",
    "landing.hero.eyebrow": "منصة الذكاء الشفاف للعقود",
    "landing.hero.title": "اطّلع على الجانبين",
    "landing.hero.subtitle": "كلاريفا هي أول منصة عقود مدعومة بالذكاء الاصطناعي تخدم المُحرّرين والموقّعين بالقدر نفسه من الذكاء — تحليل بنداً بنداً، إيجازات صوتية، وتقارير تأثير تجاري قبل التوقيع، وتدفقات موافقة بالسحب والإفلات.",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10 لغات · يدعم RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "موضع ثقة فرق قانونية وتشغيلية متحرّكة",
    "landing.features.eyebrow": "مصمَّم لغير المحامين. ومحبوب لدى المحامين.",
    "landing.features.title": "تسعة أسباب تجعل الفرق تستبدل ملفات PDF المعدّلة بكلاريفا.",
    "landing.feat.signatory.title": "تجربة الموقّع",
    "landing.feat.signatory.desc": "تحليل البنود مع/ضد من وجهة نظرك أنت — لا من وجهة نظر المُحرّر فقط. شفافية حقيقية عند التوقيع.",
    "landing.feat.audio.title": "إيجازات صوتية",
    "landing.feat.audio.desc": "تقرأ تقنية OpenAI TTS ملخصاً واضحاً في 60 ثانية. مثالي للمدراء التنفيذيين الذين لا يقرؤون العقود.",
    "landing.feat.impact.title": "تقارير الأثر التجاري",
    "landing.feat.impact.desc": "تقييم رقمي قبل التوقيع عبر 8 محاور: مالي، تشغيلي، سمعة، تنظيمي وأكثر.",
    "landing.feat.workflows.title": "تدفقات بالسحب والإفلات",
    "landing.feat.workflows.desc": "مصمم موافقات بصري مع تفرعات وقرارات ومحفّزات تلقائية وتوقيع إلكتروني وخطوات ذكاء اصطناعي.",
    "landing.feat.obligations.title": "مراقبة الالتزامات",
    "landing.feat.obligations.desc": "تتبّع اتفاقيات مستوى الخدمة والمواعيد ومخاطر الإخلال. تحليل سلسلي عبر محفظتك بالكامل.",
    "landing.feat.compliance.title": "خزانة الامتثال",
    "landing.feat.compliance.desc": "15 قالبًا مجرَّبًا: HIPAA BAA، GDPR DPA، SaaS MSA، موافقة المستشفى، المؤثّرون، توزيع موسيقى وأكثر.",
    "landing.feat.ai.title": "تحليل Claude Sonnet 4.5",
    "landing.feat.ai.desc": "ذكاء اصطناعي متقدم يحوّل اللغة القانونية إلى قرارات يمكن لمديرك المالي اعتمادها. بلا هلوسة، مع استشهادات.",
    "landing.feat.global.title": "جاهز عالميًا",
    "landing.feat.global.desc": "10 لغات، عربية RTL، عملات متعددة، إقامة بيانات في الشرق الأوسط وأوروبا وآسيا منذ اليوم الأول.",
    "landing.feat.counterparty.title": "رؤية شاملة للأطراف المقابلة",
    "landing.feat.counterparty.desc": "خريطة العلاقات وتعديلات سابقة وسجل التفاوض. اعرف مَن توقّع معه.",
    "landing.how.eyebrow": "من اللصق إلى التوقيع في أربع خطوات",
    "landing.how.title": "كيف تحوّل كلاريفا المسوَّدة إلى صفقة موقّعة.",
    "landing.how.s1.title": "ألصق أو ارفع",
    "landing.how.s1.desc": "PDF، DOCX، نص عادي، أو رابط Notion — تستوعبه كلاريفا.",
    "landing.how.s2.title": "الذكاء يحلل الجانبين",
    "landing.how.s2.desc": "يقرأ Claude كل بند من منظور المُحرّر والموقّع.",
    "landing.how.s3.title": "وجِّه للموافقة",
    "landing.how.s3.desc": "اسحب وأفلت سلسلة الموافقة — قانوني → مالي → توقيع.",
    "landing.how.s4.title": "وقّع بثقة",
    "landing.how.s4.desc": "يحصل الطرف المقابل على رؤية مع/ضد + إيجاز صوتي. لا مفاجآت.",
    "landing.industries.eyebrow": "قطاعات نخدمها",
    "landing.industries.title": "أدلّة عمل متخصصة للقطاعات الأكثر توقيعًا للعقود.",
    "landing.ind.healthcare": "الرعاية الصحية", "landing.ind.healthcare.desc": "HIPAA BAA، موافقات الجراحة، الخدمات السريرية.",
    "landing.ind.clinical": "التجارب السريرية وأبحاث الأدوية", "landing.ind.clinical.desc": "اتفاقيات الباحثين للمراحل I–IV، استمارات موافقة وتسجيل المرضى المختبَرين، عقود CRO، بروتوكولات IRB، وبنود الإفصاح عن الأحداث الجانبية.",
    "landing.ind.insurance": "شركات التأمين", "landing.ind.insurance.desc": "العروض، جداول البوالص، التظهيرات — مع فحص الإفصاح بحسن النية وتحليل بنود المطالبات.",
    "landing.ind.banking": "البنوك والإقراض", "landing.ind.banking.desc": "قروض شخصية ورهون عقارية وائتمان أعمال — مع استخراج الأقساط والتعهدات تلقائيًا.",
    "landing.ind.realestate": "العقارات والتطوير", "landing.ind.realestate.desc": "بيع على الخريطة، شراء نقدي، ضمان وعقود متوافقة مع RERA.",
    "landing.ind.leasing": "الإيجار والاستئجار", "landing.ind.leasing.desc": "إيجارات سكنية وتجارية مع تعديل CPI ودفاتر مراجعة الإيجار.",
    "landing.ind.property": "إدارة العقارات", "landing.ind.property.desc": "اتفاقيات المالك والمدير، حسابات الأمانة، ودورة الشغور.",
    "landing.ind.financial": "الخدمات المالية", "landing.ind.financial.desc": "MSA، NDA، شراكات تقنية مالية، امتثال تنظيمي.",
    "landing.ind.saas": "SaaS للمؤسسات", "landing.ind.saas.desc": "SaaS MSA، DPA، ملاحق أمنية، شهادات SOC 2.",
    "landing.ind.legal": "القانون والاستشارات", "landing.ind.legal.desc": "خطابات تكليف، مستشار خارجي، فحص تعارض المصالح.",
    "landing.ind.education": "التعليم", "landing.ind.education.desc": "ترخيص، اتفاقيات موردين، مشاركة بيانات بحثية.",
    "landing.mobile.eyebrow": "تثبيت على الجوال",
    "landing.mobile.title.l1": "وافق من هاتفك.",
    "landing.mobile.title.l2": "جاهز دون اتصال. بإحساس أصلي.",
    "landing.mobile.desc": "كلاريفا تطبيق ويب تقدمي — أضفه إلى الشاشة الرئيسية على iOS أو Android بنقرة واحدة. مستودعك مخزّن للمراجعة دون اتصال، وإشعارات الدفع تنبهك فور انتظار عقد لموافقتك.",
    "landing.mobile.feat.install.title": "تثبيت بنقرة",
    "landing.mobile.feat.install.desc": "أيقونة على الشاشة الرئيسية، بدون متجر",
    "landing.mobile.feat.offline.title": "يعمل بدون إنترنت",
    "landing.mobile.feat.offline.desc": "وضع القراءة بدون إشارة",
    "landing.mobile.feat.push.title": "تنبيهات فورية",
    "landing.mobile.feat.push.desc": "موافقات وتوقيعات وانتهاء صلاحية",
    "landing.mobile.qr.title": "امسح للفتح",
    "landing.mobile.qr.subtitle": "وجّه كاميرا هاتفك إلى رمز QR",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → مشاركة → إضافة للشاشة الرئيسية",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → القائمة → تثبيت التطبيق",
    "landing.mobile.qr.fallback": "أو زر مباشرةً",
    "landing.testimonials.eyebrow": "ماذا يقول مستخدمونا",
    "landing.testimonials.title": "«استبدلنا اثنين من المساعدين القانونيين وأداة توقيع إلكتروني.»",
    "landing.who.eyebrow": "من نحن",
    "landing.who.title": "فريق مهووس بالعقود ومؤسسون سابقون في تقنية القانون ومهندسو ذكاء اصطناعي.",
    "landing.who.desc": "تأسست كلاريفا في دبي بقناعة واحدة: يستحق الموقّعون الذكاء نفسه الذي يحظى به المُحرّرون. نبني المنصة التي تمنّينا وجودها حين كنا نقود الشؤون القانونية في شركة فينتك من الجولة D.",
    "landing.contact.eyebrow": "تحدّث إلينا",
    "landing.contact.title": "احجز عرضًا حيًا لمدة 30 دقيقة.",
    "landing.contact.desc": "أرنا أصعب عقد لديك وسنشغّله عبر كلاريفا مباشرةً. لا شرائح، لا تسجيل — المنتج فقط.",
    "landing.contact.form.name": "الاسم الكامل", "landing.contact.form.email": "البريد المهني",
    "landing.contact.form.company": "الشركة", "landing.contact.form.message": "ماذا تودّ أن ترى؟",
    "landing.contact.form.submit": "اطلب عرضًا",
    "landing.final.title": "اطّلع على عقدك القادم من الجانبين.",
    "landing.final.desc": "مساحة مجانية، ثلاثة عقود نموذجية، بدون بطاقة ائتمانية.",
    "landing.footer.tagline": "منصة الذكاء الشفاف للعقود.",
    "landing.footer.product": "المنتج", "landing.footer.company": "الشركة",
    "landing.footer.legal": "قانوني", "landing.footer.rights": "جميع الحقوق محفوظة.",
    "landing.pricing.eyebrow": "خطط لكل فريق",
    "landing.pricing.title": "تسعير بسيط وشفاف.",
    "landing.pricing.subtitle": "ابدأ مجانًا بكامل الوحدات التسع. ترقَّ عند تجاوز الحدود.",
    "landing.pricing.popular": "الأكثر شيوعًا",
    "landing.pricing.free": "مجاني", "landing.pricing.starter": "أساسي",
    "landing.pricing.pro": "احترافي", "landing.pricing.enterprise": "مؤسسات",
    "landing.pricing.cta.free": "ابدأ مجانًا",
    "landing.pricing.cta.paid": "اختر الخطة",
    "landing.pricing.cta.enterprise": "تحدّث إلى المبيعات",
    "landing.pricing.contact": "مخصّص",
    "landing.pricing.billed.monthly": "لكل مستخدم/شهريًا",
    "landing.pricing.billed.forever": "للأبد، بدون بطاقة",
    "landing.pricing.billed.contact": "حسب احتياج فريقك",
    "landing.pricing.unlimited": "غير محدود",
    "landing.pricing.contracts": "عقود",
    "landing.pricing.seats": "مقاعد للفريق",
    "landing.pricing.fineprint": "الأسعار معروضة بـ {currency}. محوّلة من الدولار بأسعار إرشادية — وتُحرّر الفاتورة بعملتك المتعاقد عليها.",
    
    "landing.process.eyebrow": "عملية كلاريفا",
    "landing.process.title.l1": "من المسوَّدة إلى التوقيع",
    "landing.process.title.l2": "عبر ست مراحل شفافة.",
    "landing.process.subtitle": "كل مرحلة موثَّقة. كل قرار للذكاء الاصطناعي مشروح. كل موقّع يرى ما تراه.",
    "landing.process.s1.title": "حمّل أو حرّر",
    "landing.process.s1.desc": "PDF أو DOCX أو ابدأ من 15 قالباً قطاعياً. جاهز للتعرّف الضوئي.",
    "landing.process.s2.title": "الذكاء يحلّل كل بند",
    "landing.process.s2.desc": "Claude Sonnet 4.5 يقيّم مخاطر كل بند، ويربطه بالالتزامات، ويترجم اللغة القانونية.",
    "landing.process.s3.title": "تقرير الأثر التجاري",
    "landing.process.s3.desc": "بطاقة تقييم على 8 محاور: مالي، تشغيلي، سمعة وأكثر — في ثوانٍ.",
    "landing.process.s4.title": "إيجاز صوتي",
    "landing.process.s4.desc": "ملخص صوتي بـ OpenAI TTS لمدة 60 ثانية. رابط قابل للمشاركة مع المسؤولين والأطراف.",
    "landing.process.s5.title": "وجِّه عبر التدفق",
    "landing.process.s5.desc": "مصمم بالسحب والإفلات: موافقات، تفرعات قرار، مراجعات متوازية، محفّزات تلقائية.",
    "landing.process.s6.title": "وقّع مع سجل تدقيق",
    "landing.process.s6.desc": "توقيع إلكتروني عبر QR، سجل غير قابل للعبث، ومراقبة الالتزامات تبدأ لحظة التوقيع.",
    "landing.walkthrough.eyebrow": "جولة في دقيقتين",
    "landing.walkthrough.title": "شاهد كلاريفا يحلّل اتفاقية MSA حقيقية.",
    "landing.walkthrough.subtitle": "شاهد عقدًا كاملاً ينتقل من الإيداع إلى التوقيع في أقل من دقيقتين — مع شرح كل قرار للذكاء الاصطناعي.",
    "landing.walkthrough.duration": "2:14 · مع تعليق صوتي",
    "landing.walkthrough.kpi1.n": "2.1×",
    "landing.walkthrough.kpi1.l": "دورات تفاوض أسرع",
    "landing.walkthrough.kpi2.n": "68%",
    "landing.walkthrough.kpi2.l": "التزامات أقل مفقودة",
    "landing.walkthrough.kpi3.l": "متوسط التوفير في السنة الأولى",
    "landing.testimonials.t1.quote": "انتقل مستشارنا العام من قراءة 40 عقدًا أسبوعيًا إلى مراجعة تقارير أثر كلاريفا في 10 دقائق لكل عقد. أنجزنا صفقات أكثر بثلاث مرات في الربع الماضي.",
    "landing.testimonials.t1.name": "مايا تشين",
    "landing.testimonials.t1.role": "المدير التشغيلي، CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "خاصية الإيجاز الصوتي وحدها تستحق ثمن المنصة. مديرتي التنفيذية تستمع إليها أثناء توجهها إلى العمل.",
    "landing.testimonials.t2.name": "لويس ريفيرا",
    "landing.testimonials.t2.role": "نائب رئيس العمليات القانونية، Harmonic Wave Records",
    "landing.testimonials.t3.quote": "أخيرًا أداة لا تعامل الموقّعين كأمر ثانوي. الموردون يثقون بنا أكثر لأنهم يحصلون على تحليل شفاف للمخاطر قبل التوقيع.",
    "landing.testimonials.t3.name": "بريا رحمن",
    "landing.testimonials.t3.role": "مؤسسة، Arcadia Health Collective",
    "landing.who.title2": "فريق من خبراء قانونيين وباحثي تعلّم الآلة ومصممين — توحّدهم قناعة واحدة.",
    "landing.who.desc2": "يجب ألّا تكون العقود أسلحة من جانب واحد. ينبغي أن تكون فهماً مشتركاً بين طرفين، كلاهما مزود بنفس الوضوح. وُلدت كلاريفا داخل مكتب محاماة سئم رؤية عملائه يوقّعون أشياء لا يفهمونها. نحن هنا لجعل ذلك من الماضي.",
    "landing.who.pillar1.title": "الشفافية أولاً",
    "landing.who.pillar1.desc": "نرفض إخفاء منطق الذكاء الاصطناعي. كل تقييم وكل تنبيه مخاطر مرفق باستشهاد من الوثيقة الأصلية.",
    "landing.who.pillar2.title": "الموقّعون مهمون",
    "landing.who.pillar2.desc": "العقود تُلزم الطرفين. نبني للإنسان الذي يوقّع أخيرًا، لا للمحامي الذي يحرّر أولاً.",
    "landing.who.pillar3.title": "سرعة على مستوى المؤسسات",
    "landing.who.pillar3.desc": "تحليل البنود في أقل من ثانية، صوت متدفّق، نشر بدون توقّف. مبني على Claude 4.5 + FastAPI + React.",
    "landing.contact.eyebrow2": "لنتحدّث",
    "landing.contact.title2": "احجز عرضًا كاملاً، أو رتّب جلسة eSession.",
    "landing.contact.desc2": "أخبرنا قليلاً عن فريقك — سنطابقك مع متخصص عمل في قطاعك، ونعدّ لك جولة مخصّصة باستخدام عقدك الفعلي (تحت اتفاقية عدم إفصاح بالطبع).",
    "landing.contact.bullet1": "جولة منتج مخصّصة لمدة 30 دقيقة",
    "landing.contact.bullet2": "تحليل بنود مباشر على عقدك الحقيقي",
    "landing.contact.bullet3": "دليل التنفيذ والإطلاق",
    "landing.contact.bullet4": "نموذج عائد استثمار مخصّص لإنفاقك القانوني",
    "landing.contact.intent.demo": "عرض كامل",
    "landing.contact.intent.esession": "ترتيب eSession",
    "landing.contact.intent.sales": "تواصل مع المبيعات",
    "landing.contact.placeholder.name": "جين المستشارة",
    "landing.contact.placeholder.email": "jane@company.com",
    "landing.contact.placeholder.company": "اسم الشركة",
    "landing.contact.placeholder.message": "نقيّم منصة ذكاء العقود لفريق عملياتنا القانونية...",
    "landing.contact.field.preferred_date": "التاريخ المفضّل",
    "landing.contact.field.preferred_time": "الوقت المفضّل",
    "landing.contact.field.message": "ما الذي تودّ تحقيقه؟",
    "landing.contact.privacy": "نرد خلال ساعتي عمل. لا رسائل مزعجة أبدًا.",
    "landing.contact.submit.demo": "اطلب عرضًا كاملاً",
    "landing.contact.submit.esession": "احجز eSession",
    "landing.contact.submit.sales": "تواصل مع المبيعات",
    "landing.contact.submitting": "جارٍ الإرسال…",
    "landing.contact.success.title": "تم.",
    "landing.contact.success.desc": "سنتواصل معك خلال ساعتي عمل برابط تقويم.",
    "landing.contact.toast.required": "الاسم والبريد الإلكتروني مطلوبان",
    "landing.contact.toast.success": "شكرًا! سنتواصل معك قريبًا.",
    "landing.contact.toast.esession": "تم استلام طلب eSession — سنؤكده خلال ساعتي عمل",
    "landing.final.title2.l1": "ابدأ برؤية الجانبين",
    "landing.final.title2.l2": "في عقدك القادم.",
    "landing.final.desc2": "مجاني للبدء. بدون بطاقة ائتمان. التسجيل أقل من 30 ثانية ويزرع لك مساحة عمل تجريبية كاملة.",
    "landing.final.cta.start": "ابدأ مجانًا الآن",
    "landing.final.cta.demo": "جرّب demo@clariva.ai",
    "landing.footer.tagline2": "ذكاء عقود شفاف بالذكاء الاصطناعي. للمحرّرين والموقّعين على حدّ سواء.",
    "landing.footer.product.items": "الميزات,التدفقات,الإيجازات الصوتية,الأثر التجاري",
    "landing.footer.company.items": "من نحن,الوظائف,المواد الإعلامية,الأمان",
    "landing.footer.legal.items": "الشروط,الخصوصية,DPA,تقرير SOC 2",
    "landing.footer.copyright": "© 2026 كلاريفا. ذكاء عقود شفاف بالذكاء الاصطناعي.",
    "landing.footer.madein": "صُنع بدقة في سان فرانسيسكو · لندن · دبي",
        "landing.pricing.feat.all_9_modules": "الوحدات التسع كاملة",
    "landing.pricing.feat.ai_contract_analysis": "تحليل العقود بالذكاء الاصطناعي",
    "landing.pricing.feat.business_impact_reports": "تقارير الأثر التجاري",
    "landing.pricing.feat.audio_briefings_limited": "إيجازات صوتية (محدودة)",
    "landing.pricing.feat.email_support": "دعم عبر البريد الإلكتروني",
    "landing.pricing.feat.everything_in_free": "كل ما في الخطة المجانية",
    "landing.pricing.feat.everything_in_starter": "كل ما في خطة Starter",
    "landing.pricing.feat.everything_in_professional": "كل ما في الخطة الاحترافية",
    "landing.pricing.feat.5_team_seats": "5 مقاعد للفريق",
    "landing.pricing.feat.25_team_seats": "25 مقعدًا للفريق",
    "landing.pricing.feat.10_integrations": "10 تكاملات",
    "landing.pricing.feat.custom_templates": "قوالب مخصّصة",
    "landing.pricing.feat.priority_email_support": "دعم بريد ذو أولوية",
    "landing.pricing.feat.api_access": "وصول API",
    "landing.pricing.feat.custom_rbac_roles": "أدوار RBAC مخصّصة",
    "landing.pricing.feat.sso_google_okta": "SSO عبر Google و Okta",
    "landing.pricing.feat.sla_99_9": "اتفاقية مستوى خدمة 99.9%",
    "landing.pricing.feat.dedicated_csm": "مدير نجاح عملاء مخصّص",
    "landing.pricing.feat.cross_contract_ripple_analysis": "تحليل سلسلي عبر العقود",
    "landing.pricing.feat.approval_workflows": "تدفقات الموافقة",
    "landing.pricing.feat.unlimited_everything": "كل شيء بلا حدود",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "ذكاء اصطناعي مُدرَّب لكل عميل",
    "landing.pricing.feat.byok_encryption": "تشفير بمفاتيحك (BYOK)",
    "landing.pricing.feat.regional_data_residency": "إقامة بيانات إقليمية",
    "landing.pricing.feat.24_7_phone_support": "دعم هاتفي على مدار الساعة",
    "landing.pricing.feat.custom_compliance_packs": "حزم امتثال مخصّصة",
    "landing.pricing.feat.soc_2_type_ii_report": "تقرير SOC 2 Type II",
    "landing.pricing.feat.red_team_audit": "تدقيق فريق أحمر",
    "locale.banner.detected": "ضبطنا اللغة إلى {lang} بناءً على موقعك.",
    "locale.banner.undo": "العودة إلى الإنجليزية",
    "locale.banner.keep": "الاحتفاظ بـ {lang}",
  },

  /* ============================ FRENCH ============================ */
  fr: {
    "nav.dashboard": "Tableau de bord", "nav.studio": "Studio de Contrats", "nav.repository": "Référentiel",
    "nav.workflows": "Workflows", "nav.impact": "Impact Commercial", "nav.monitoring": "Surveillance",
    "nav.counterparty": "Contreparties", "nav.integrations": "Intégrations",
    "nav.analytics": "Analyses", "nav.team": "Équipe & Rôles", "nav.subscription": "Abonnement",
    "nav.compliance": "Conformité", "nav.settings": "Paramètres",
    "common.save": "Enregistrer", "common.cancel": "Annuler", "common.new": "Nouveau",
    "common.ask": "Demandez à Clariva…", "common.signin": "Se connecter", "common.getstarted": "Commencer",
    "common.bookdemo": "Réserver une démo", "common.startfree": "Essai gratuit — sans carte",
    "common.watch": "Voir la démo de 2 min", "common.theme": "Thème",
    "landing.nav.features": "Fonctionnalités", "landing.nav.howitworks": "Comment ça marche",
    "landing.nav.industries": "Secteurs", "landing.nav.mobile": "App mobile",
        "landing.nav.product": "Produit",
    "landing.nav.solutions": "Solutions",
    "landing.nav.testdemo": "Tester la démo",
    "landing.nav.pricing": "Tarifs",
    "landing.nav.who": "Qui sommes-nous", "landing.nav.contact": "Contact",
    "landing.hero.eyebrow": "La plateforme d'intelligence contractuelle transparente",
    "landing.hero.title": "Voyez les deux côtés",
    "landing.hero.subtitle": "Clariva est la première plateforme contractuelle IA qui sert les rédacteurs ET les signataires avec la même intelligence — analyse clause par clause, briefings audio, rapports d'impact business avant signature, et workflows d'approbation en glisser-déposer.",
    "landing.hero.badge.soc": "SOC 2 · RGPD · HIPAA",
    "landing.hero.badge.lang": "10 langues · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "Adopté par des équipes juridiques et ops agiles",
    "landing.features.eyebrow": "Conçu pour les non-juristes. Adoré par les juristes.",
    "landing.features.title": "Neuf raisons de remplacer vos PDF annotés par Clariva.",
    "landing.feat.signatory.title": "Expérience signataire",
    "landing.feat.signatory.desc": "Décomposition pour/contre des clauses depuis VOTRE point de vue — pas seulement celui du rédacteur. Enfin, transparence à la signature.",
    "landing.feat.audio.title": "Briefings audio",
    "landing.feat.audio.desc": "OpenAI TTS lit un résumé clair de 60 secondes. Parfait pour les dirigeants qui ne lisent pas les contrats.",
    "landing.feat.impact.title": "Rapports d'impact business",
    "landing.feat.impact.desc": "Notation pré-signature sur 8 axes : financier, opérationnel, réputation, réglementaire et plus.",
    "landing.feat.workflows.title": "Workflows en glisser-déposer",
    "landing.feat.workflows.desc": "Concepteur visuel d'approbations avec branches, déclencheurs, e-signature et étapes IA.",
    "landing.feat.obligations.title": "Suivi des obligations",
    "landing.feat.obligations.desc": "Suivez les SLA, échéances et risques de manquement. Analyse en cascade sur tout votre portefeuille.",
    "landing.feat.compliance.title": "Coffre conformité",
    "landing.feat.compliance.desc": "15 modèles éprouvés : HIPAA BAA, RGPD DPA, MSA SaaS, consentement hospitalier, influenceur, distribution musicale et plus.",
    "landing.feat.ai.title": "Analyse Claude Sonnet 4.5",
    "landing.feat.ai.desc": "Une IA de pointe traduit le jargon juridique en décisions que votre directeur financier peut valider. Sans hallucinations, avec citations.",
    "landing.feat.global.title": "Prêt pour le monde",
    "landing.feat.global.desc": "10 langues, arabe RTL, multi-devises, hébergement MEA + UE + APAC dès le premier jour.",
    "landing.feat.counterparty.title": "Contrepartie 360°",
    "landing.feat.counterparty.desc": "Graphe relationnel, anciens redlines, historique de négociation. Sachez avec qui vous signez.",
    "landing.how.eyebrow": "Du collage à la signature en quatre étapes",
    "landing.how.title": "Comment Clariva transforme un brouillon en accord signé.",
    "landing.how.s1.title": "Collez ou téléversez",
    "landing.how.s1.desc": "PDF, DOCX, texte brut, ou un lien Notion — Clariva l'absorbe.",
    "landing.how.s2.title": "L'IA analyse les deux côtés",
    "landing.how.s2.desc": "Claude lit chaque clause depuis les perspectives rédacteur ET signataire.",
    "landing.how.s3.title": "Acheminez l'approbation",
    "landing.how.s3.desc": "Glissez-déposez la chaîne d'approbation — Juridique → Finance → Signature.",
    "landing.how.s4.title": "Signez en confiance",
    "landing.how.s4.desc": "Les contreparties obtiennent la vue pour/contre + briefing audio. Aucune surprise.",
    "landing.industries.eyebrow": "Secteurs servis",
    "landing.industries.title": "Des playbooks spécialisés pour les secteurs qui signent le plus de contrats.",
    "landing.ind.healthcare": "Santé", "landing.ind.healthcare.desc": "HIPAA BAA, consentement chirurgical, services cliniques.",
    "landing.ind.clinical": "Essais cliniques & recherche pharma", "landing.ind.clinical.desc": "Accords investigateurs phases I–IV, consentements éclairés et inscription des patients tests, contrats CRO, protocoles prêts pour CPP, clauses de divulgation d'événements indésirables.",
    "landing.ind.insurance": "Compagnies d'assurance", "landing.ind.insurance.desc": "Propositions, conditions de police, avenants — avec scan de bonne foi et extraction des clauses de sinistres.",
    "landing.ind.banking": "Banque & Crédit", "landing.ind.banking.desc": "Prêts personnels, hypothèques, crédit pro — avec extraction automatique des mensualités et engagements.",
    "landing.ind.realestate": "Immobilier & Promotion", "landing.ind.realestate.desc": "Vente sur plan, achats au comptant, séquestre et accords RERA.",
    "landing.ind.leasing": "Location & Bail", "landing.ind.leasing.desc": "Baux résidentiels et commerciaux avec indexation IPC.",
    "landing.ind.property": "Gestion immobilière", "landing.ind.property.desc": "Mandats de gestion, comptes séquestres, cycles de vacance.",
    "landing.ind.financial": "Services financiers", "landing.ind.financial.desc": "MSA, NDA, partenariats fintech, conformité réglementaire.",
    "landing.ind.saas": "SaaS Entreprise", "landing.ind.saas.desc": "MSA SaaS, DPA, addenda sécurité, attestations SOC 2.",
    "landing.ind.legal": "Droit & Conseil", "landing.ind.legal.desc": "Lettres de mission, conseil externe, vérifications de conflit.",
    "landing.ind.education": "Éducation", "landing.ind.education.desc": "Licences, accords fournisseurs, partage de données de recherche.",
    "landing.mobile.eyebrow": "Installation mobile",
    "landing.mobile.title.l1": "Approuvez depuis votre mobile.",
    "landing.mobile.title.l2": "Hors ligne. Sensation native.",
    "landing.mobile.desc": "Clariva est une Progressive Web App — ajoutez-la à l'écran d'accueil iOS ou Android en un geste. Votre référentiel est mis en cache pour consultation hors ligne, et les notifications push vous préviennent dès qu'un contrat attend votre approbation.",
    "landing.mobile.feat.install.title": "Installation en un clic",
    "landing.mobile.feat.install.desc": "Icône d'écran d'accueil, sans store",
    "landing.mobile.feat.offline.title": "Prêt hors ligne",
    "landing.mobile.feat.offline.desc": "Lecture sans signal",
    "landing.mobile.feat.push.title": "Alertes push",
    "landing.mobile.feat.push.desc": "Approbations, signatures, expirations",
    "landing.mobile.qr.title": "Scannez pour ouvrir",
    "landing.mobile.qr.subtitle": "Pointez l'appareil photo vers un QR code",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → Partager → Ajouter à l'accueil",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → menu → Installer",
    "landing.mobile.qr.fallback": "Ou rendez-vous sur",
    "landing.testimonials.eyebrow": "Ce que disent nos clients",
    "landing.testimonials.title": "« Nous avons remplacé deux assistants juridiques et un outil d'e-signature. »",
    "landing.who.eyebrow": "Qui sommes-nous",
    "landing.who.title": "Une équipe d'obsédés du contrat, ex-fondateurs legal-tech et ingénieurs IA.",
    "landing.who.desc": "Clariva a été fondée à Dubaï avec une conviction : les signataires méritent la même intelligence que les rédacteurs. Nous construisons la plateforme dont nous rêvions quand nous dirigions le juridique d'une fintech série D.",
    "landing.contact.eyebrow": "Parlons-en",
    "landing.contact.title": "Réservez une démo live de 30 minutes.",
    "landing.contact.desc": "Montrez-nous votre contrat le plus difficile et nous le passerons dans Clariva en direct. Pas de slides, pas d'enregistrement — juste le produit.",
    "landing.contact.form.name": "Nom complet", "landing.contact.form.email": "Email professionnel",
    "landing.contact.form.company": "Entreprise", "landing.contact.form.message": "Que souhaitez-vous voir ?",
    "landing.contact.form.submit": "Demander une démo",
    "landing.final.title": "Voyez votre prochain contrat des deux côtés.",
    "landing.final.desc": "Espace gratuit, trois contrats d'exemple, sans carte bancaire.",
    "landing.footer.tagline": "La plateforme d'intelligence contractuelle transparente.",
    "landing.footer.product": "Produit", "landing.footer.company": "Société",
    "landing.footer.legal": "Mentions légales", "landing.footer.rights": "Tous droits réservés.",
    "landing.pricing.eyebrow": "Des plans pour chaque équipe",
    "landing.pricing.title": "Tarification simple et transparente.",
    "landing.pricing.subtitle": "Commencez gratuitement avec les 9 modules. Passez à un plan supérieur quand vous dépassez les limites.",
    "landing.pricing.popular": "Le plus populaire",
    "landing.pricing.free": "Gratuit", "landing.pricing.starter": "Starter",
    "landing.pricing.pro": "Professionnel", "landing.pricing.enterprise": "Entreprise",
    "landing.pricing.cta.free": "Commencer gratuitement",
    "landing.pricing.cta.paid": "Choisir ce plan",
    "landing.pricing.cta.enterprise": "Parler aux ventes",
    "landing.pricing.contact": "Sur mesure",
    "landing.pricing.billed.monthly": "par utilisateur / mois",
    "landing.pricing.billed.forever": "à vie, sans carte",
    "landing.pricing.billed.contact": "adapté à votre équipe",
    "landing.pricing.unlimited": "Illimité",
    "landing.pricing.contracts": "contrats",
    "landing.pricing.seats": "sièges d'équipe",
    "landing.pricing.fineprint": "Tarifs affichés en {currency}. Convertis depuis l'USD à titre indicatif — facturation dans votre devise contractuelle.",
    
    "landing.process.eyebrow": "Le processus Clariva",
    "landing.process.title.l1": "Du brouillon à la signature,",
    "landing.process.title.l2": "en six étapes transparentes.",
    "landing.process.subtitle": "Chaque étape est instrumentée. Chaque décision IA est expliquée. Chaque signataire voit ce que vous voyez.",
    "landing.process.s1.title": "Téléverser ou rédiger",
    "landing.process.s1.desc": "PDF, DOCX, ou partez d'un des 15 modèles sectoriels. OCR prêt à l'emploi.",
    "landing.process.s2.title": "L'IA dissèque chaque clause",
    "landing.process.s2.desc": "Claude Sonnet 4.5 note chaque clause, la lie aux obligations, traduit le jargon juridique.",
    "landing.process.s3.title": "Rapport d'impact business",
    "landing.process.s3.desc": "Carte de notation sur 8 axes : financier, opérationnel, réputation et plus — en quelques secondes.",
    "landing.process.s4.title": "Briefing audio",
    "landing.process.s4.desc": "Résumé vocal de 60 secondes via OpenAI TTS. Lien partageable pour les dirigeants et contreparties.",
    "landing.process.s5.title": "Acheminement par workflow",
    "landing.process.s5.desc": "Concepteur glisser-déposer : approbations, branches de décision, revues parallèles, déclencheurs auto.",
    "landing.process.s6.title": "Signer avec piste d'audit",
    "landing.process.s6.desc": "Signature électronique partageable par QR, journal infalsifiable, surveillance des obligations dès la signature.",
    "landing.walkthrough.eyebrow": "Démo de 2 minutes",
    "landing.walkthrough.title": "Voyez Clariva disséquer un vrai MSA.",
    "landing.walkthrough.subtitle": "Regardez un contrat passer du dépôt à la signature en moins de deux minutes — chaque décision IA expliquée.",
    "landing.walkthrough.duration": "2:14 · Avec voix off",
    "landing.walkthrough.kpi1.n": "2,1×",
    "landing.walkthrough.kpi1.l": "Cycles de négociation plus rapides",
    "landing.walkthrough.kpi2.n": "68 %",
    "landing.walkthrough.kpi2.l": "Moins d'obligations manquées",
    "landing.walkthrough.kpi3.l": "Économies moyennes la 1ʳᵉ année",
    "landing.testimonials.t1.quote": "Notre directeur juridique est passé de 40 contrats lus par semaine à la revue des rapports d'impact Clariva en 10 minutes chacun. Nous avons signé 3× plus de deals au dernier trimestre.",
    "landing.testimonials.t1.name": "Maya Chen",
    "landing.testimonials.t1.role": "COO, CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "Le briefing audio à lui seul rentabilise toute la plateforme. Ma PDG les écoute pendant ses trajets.",
    "landing.testimonials.t2.name": "Luis Rivera",
    "landing.testimonials.t2.role": "VP Legal Ops, Harmonic Wave Records",
    "landing.testimonials.t3.quote": "Enfin un outil qui ne traite pas les signataires comme un détail. Nos fournisseurs nous font davantage confiance car ils reçoivent une analyse de risques transparente avant de signer.",
    "landing.testimonials.t3.name": "Priya Rahman",
    "landing.testimonials.t3.role": "Fondatrice, Arcadia Health Collective",
    "landing.who.title2": "Une équipe d'opérateurs juridiques, chercheurs ML et designers — unis par une conviction.",
    "landing.who.desc2": "Les contrats ne devraient pas être des armes unilatérales. Ils devraient être une compréhension partagée entre deux parties, équipées de la même clarté. Clariva est née dans un cabinet d'avocats fatigué de voir des clients signer des choses qu'ils ne comprenaient pas. Nous sommes là pour en finir.",
    "landing.who.pillar1.title": "La transparence d'abord",
    "landing.who.pillar1.desc": "Nous refusons de cacher le raisonnement de l'IA. Chaque score, chaque alerte vient avec une citation du document source.",
    "landing.who.pillar2.title": "Les signataires comptent",
    "landing.who.pillar2.desc": "Les contrats lient les deux parties. Nous bâtissons pour l'humain qui signe en dernier, pas seulement pour l'avocat qui rédige en premier.",
    "landing.who.pillar3.title": "Vitesse de niveau entreprise",
    "landing.who.pillar3.desc": "Analyse de clauses en moins d'une seconde, audio en streaming, déploiements sans interruption. Sur Claude 4.5 + FastAPI + React.",
    "landing.contact.eyebrow2": "Parlons-en",
    "landing.contact.title2": "Réservez une démo complète, ou planifiez une eSession.",
    "landing.contact.desc2": "Parlez-nous de votre équipe — nous vous mettrons en relation avec un spécialiste de votre secteur et préparerons une démo personnalisée avec votre vrai contrat (sous NDA, bien sûr).",
    "landing.contact.bullet1": "Démo produit personnalisée de 30 min",
    "landing.contact.bullet2": "Analyse de clauses en direct sur votre vrai contrat",
    "landing.contact.bullet3": "Plan d'implémentation et de déploiement",
    "landing.contact.bullet4": "Modèle ROI sur mesure pour votre budget juridique",
    "landing.contact.intent.demo": "Démo complète",
    "landing.contact.intent.esession": "Planifier une eSession",
    "landing.contact.intent.sales": "Contacter les ventes",
    "landing.contact.placeholder.name": "Jeanne Dupont",
    "landing.contact.placeholder.email": "jeanne@entreprise.com",
    "landing.contact.placeholder.company": "Nom de l'entreprise",
    "landing.contact.placeholder.message": "Nous évaluons l'intelligence contractuelle pour notre équipe legal ops...",
    "landing.contact.field.preferred_date": "Date souhaitée",
    "landing.contact.field.preferred_time": "Heure souhaitée",
    "landing.contact.field.message": "Que souhaitez-vous accomplir ?",
    "landing.contact.privacy": "Réponse en 2 heures ouvrées. Pas de spam, jamais.",
    "landing.contact.submit.demo": "Demander une démo complète",
    "landing.contact.submit.esession": "Réserver l'eSession",
    "landing.contact.submit.sales": "Contacter les ventes",
    "landing.contact.submitting": "Envoi…",
    "landing.contact.success.title": "C'est noté.",
    "landing.contact.success.desc": "Nous reviendrons vers vous sous 2 heures ouvrées avec un lien de calendrier.",
    "landing.contact.toast.required": "Le nom et l'email sont requis",
    "landing.contact.toast.success": "Merci ! Nous vous contactons rapidement.",
    "landing.contact.toast.esession": "Demande d'eSession reçue — confirmation sous 2 heures ouvrées",
    "landing.final.title2.l1": "Voyez les deux côtés",
    "landing.final.title2.l2": "de votre prochain contrat.",
    "landing.final.desc2": "Gratuit pour démarrer. Sans carte bancaire. Inscription en moins de 30 secondes, espace de démo complet inclus.",
    "landing.final.cta.start": "Démarrer gratuitement",
    "landing.final.cta.demo": "Tester demo@clariva.ai",
    "landing.footer.tagline2": "Intelligence contractuelle IA transparente. Pour rédacteurs et signataires, à parts égales.",
    "landing.footer.product.items": "Fonctionnalités,Workflows,Briefings audio,Impact business",
    "landing.footer.company.items": "Qui sommes-nous,Carrières,Kit presse,Sécurité",
    "landing.footer.legal.items": "Conditions,Confidentialité,DPA,Rapport SOC 2",
    "landing.footer.copyright": "© 2026 Clariva. Intelligence contractuelle IA transparente.",
    "landing.footer.madein": "Conçu avec précision à SF · Londres · Dubaï",
        "landing.pricing.feat.all_9_modules": "Les 9 modules",
    "landing.pricing.feat.ai_contract_analysis": "Analyse contractuelle par IA",
    "landing.pricing.feat.business_impact_reports": "Rapports d'impact business",
    "landing.pricing.feat.audio_briefings_limited": "Briefings audio (limité)",
    "landing.pricing.feat.email_support": "Support par email",
    "landing.pricing.feat.everything_in_free": "Tout du plan Gratuit",
    "landing.pricing.feat.everything_in_starter": "Tout du plan Starter",
    "landing.pricing.feat.everything_in_professional": "Tout du plan Professionnel",
    "landing.pricing.feat.5_team_seats": "5 sièges d'équipe",
    "landing.pricing.feat.25_team_seats": "25 sièges d'équipe",
    "landing.pricing.feat.10_integrations": "10 intégrations",
    "landing.pricing.feat.custom_templates": "Modèles personnalisés",
    "landing.pricing.feat.priority_email_support": "Support email prioritaire",
    "landing.pricing.feat.api_access": "Accès API",
    "landing.pricing.feat.custom_rbac_roles": "Rôles RBAC personnalisés",
    "landing.pricing.feat.sso_google_okta": "SSO (Google, Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99,9 %",
    "landing.pricing.feat.dedicated_csm": "CSM dédié",
    "landing.pricing.feat.cross_contract_ripple_analysis": "Analyse en cascade inter-contrats",
    "landing.pricing.feat.approval_workflows": "Workflows d'approbation",
    "landing.pricing.feat.unlimited_everything": "Tout en illimité",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "IA fine-tunée par locataire",
    "landing.pricing.feat.byok_encryption": "Chiffrement BYOK",
    "landing.pricing.feat.regional_data_residency": "Résidence régionale des données",
    "landing.pricing.feat.24_7_phone_support": "Support téléphonique 24/7",
    "landing.pricing.feat.custom_compliance_packs": "Packs de conformité sur mesure",
    "landing.pricing.feat.soc_2_type_ii_report": "Rapport SOC 2 Type II",
    "landing.pricing.feat.red_team_audit": "Audit Red Team",
    "locale.banner.detected": "Nous avons défini votre langue sur {lang} selon votre localisation.",
    "locale.banner.undo": "Repasser en anglais",
    "locale.banner.keep": "Garder {lang}",
  },

  /* ============================ SPANISH ============================ */
  es: {
    "nav.dashboard": "Panel", "nav.studio": "Estudio de Contratos", "nav.repository": "Repositorio",
    "nav.workflows": "Flujos", "nav.impact": "Impacto Empresarial", "nav.monitoring": "Monitoreo",
    "nav.counterparty": "Contrapartes", "nav.integrations": "Integraciones",
    "nav.analytics": "Analítica", "nav.team": "Equipo y Roles", "nav.subscription": "Suscripción",
    "nav.compliance": "Cumplimiento", "nav.settings": "Configuración",
    "common.save": "Guardar", "common.cancel": "Cancelar", "common.new": "Nuevo",
    "common.ask": "Pregunta a Clariva…", "common.signin": "Iniciar sesión", "common.getstarted": "Empezar",
    "common.bookdemo": "Reservar una demo", "common.startfree": "Empieza gratis — sin tarjeta",
    "common.watch": "Ver demo de 2 min", "common.theme": "Tema",
    "landing.nav.features": "Funciones", "landing.nav.howitworks": "Cómo funciona",
    "landing.nav.industries": "Sectores", "landing.nav.mobile": "App móvil",
        "landing.nav.product": "Producto",
    "landing.nav.solutions": "Soluciones",
    "landing.nav.testdemo": "Probar la demo",
    "landing.nav.pricing": "Precios",
    "landing.nav.who": "Quiénes somos", "landing.nav.contact": "Contacto",
    "landing.hero.eyebrow": "La plataforma de inteligencia contractual transparente",
    "landing.hero.title": "Mira ambos lados",
    "landing.hero.subtitle": "Clariva es la primera plataforma contractual con IA que atiende a redactores y firmantes con la misma inteligencia — análisis cláusula por cláusula, resúmenes en audio, informes de impacto antes de firmar y flujos de aprobación arrastrar y soltar.",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10 idiomas · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "Confían en nosotros equipos legales y de operaciones ágiles",
    "landing.features.eyebrow": "Pensado para no-abogados. Querido por legales.",
    "landing.features.title": "Nueve razones para dejar los PDF marcados y usar Clariva.",
    "landing.feat.signatory.title": "Experiencia del firmante",
    "landing.feat.signatory.desc": "Análisis a favor/en contra desde TU perspectiva — no solo la del redactor. Por fin, transparencia al firmar.",
    "landing.feat.audio.title": "Resúmenes en audio",
    "landing.feat.audio.desc": "OpenAI TTS lee un resumen claro de 60 segundos. Ideal para directivos que no leen contratos.",
    "landing.feat.impact.title": "Informes de impacto",
    "landing.feat.impact.desc": "Puntuación previa a firma en 8 ejes: financiero, operativo, reputacional, regulatorio y más.",
    "landing.feat.workflows.title": "Flujos arrastrar y soltar",
    "landing.feat.workflows.desc": "Diseñador visual de aprobaciones con bifurcaciones, disparadores, e-firma y pasos con IA.",
    "landing.feat.obligations.title": "Monitoreo de obligaciones",
    "landing.feat.obligations.desc": "Sigue SLA, plazos y riesgo de incumplimiento. Análisis en cascada sobre tu cartera.",
    "landing.feat.compliance.title": "Bóveda de cumplimiento",
    "landing.feat.compliance.desc": "15 plantillas probadas: HIPAA BAA, GDPR DPA, MSA SaaS, consentimiento hospitalario, influencer, distribución musical y más.",
    "landing.feat.ai.title": "Análisis con Claude Sonnet 4.5",
    "landing.feat.ai.desc": "IA de frontera traduce el lenguaje legal a decisiones que tu CFO puede aprobar. Sin alucinaciones, con citas.",
    "landing.feat.global.title": "Listo para el mundo",
    "landing.feat.global.desc": "10 idiomas, árabe RTL, multi-divisa, residencia de datos MEA + UE + APAC desde el día uno.",
    "landing.feat.counterparty.title": "Contraparte 360°",
    "landing.feat.counterparty.desc": "Grafo de relaciones, redlines pasados, historial de negociación. Conoce con quién firmas.",
    "landing.how.eyebrow": "Del pegado a la firma en cuatro pasos",
    "landing.how.title": "Cómo Clariva convierte un borrador en un acuerdo firmado.",
    "landing.how.s1.title": "Pega o sube",
    "landing.how.s1.desc": "PDF, DOCX, texto plano, incluso un enlace de Notion — Clariva lo procesa.",
    "landing.how.s2.title": "La IA analiza los dos lados",
    "landing.how.s2.desc": "Claude lee cada cláusula desde la perspectiva del redactor Y del firmante.",
    "landing.how.s3.title": "Enruta para aprobación",
    "landing.how.s3.desc": "Arrastra la cadena de aprobación — Legal → Finanzas → Firma.",
    "landing.how.s4.title": "Firma con confianza",
    "landing.how.s4.desc": "Las contrapartes ven a favor/en contra + resumen en audio. Sin sorpresas.",
    "landing.industries.eyebrow": "Sectores que servimos",
    "landing.industries.title": "Playbooks especializados para los sectores que más papel firman.",
    "landing.ind.healthcare": "Salud", "landing.ind.healthcare.desc": "HIPAA BAA, consentimientos quirúrgicos, servicios clínicos.",
    "landing.ind.clinical": "Ensayos clínicos e investigación farmacéutica", "landing.ind.clinical.desc": "Acuerdos de investigador fases I–IV, consentimiento e inscripción de pacientes de prueba, contratos CRO, protocolos listos para CEI, cláusulas de divulgación de eventos adversos.",
    "landing.ind.insurance": "Aseguradoras", "landing.ind.insurance.desc": "Propuestas, condicionados, endosos — con escaneo de buena fe y extracción de cláusulas de siniestros.",
    "landing.ind.banking": "Banca y Préstamos", "landing.ind.banking.desc": "Préstamos personales, hipotecas, crédito empresarial — con extracción automática de cuotas y compromisos.",
    "landing.ind.realestate": "Inmobiliaria y Desarrollo", "landing.ind.realestate.desc": "Venta sobre plano, compras al contado, escrow y acuerdos compatibles con RERA.",
    "landing.ind.leasing": "Arrendamiento", "landing.ind.leasing.desc": "Arrendamientos residenciales y comerciales con escalado IPC.",
    "landing.ind.property": "Administración de propiedades", "landing.ind.property.desc": "Acuerdos propietario-administrador, cuentas fiduciarias, ciclos de vacancia.",
    "landing.ind.financial": "Servicios financieros", "landing.ind.financial.desc": "MSA, NDA, alianzas fintech, cumplimiento regulatorio.",
    "landing.ind.saas": "SaaS Empresarial", "landing.ind.saas.desc": "MSA SaaS, DPA, anexos de seguridad, atestaciones SOC 2.",
    "landing.ind.legal": "Legal y Asesoría", "landing.ind.legal.desc": "Cartas de encargo, asesoría externa, conflictos de interés.",
    "landing.ind.education": "Educación", "landing.ind.education.desc": "Licencias, acuerdos con proveedores, intercambio de datos de investigación.",
    "landing.mobile.eyebrow": "Instalar en móvil",
    "landing.mobile.title.l1": "Aprueba desde tu móvil.",
    "landing.mobile.title.l2": "Sin conexión. Sensación nativa.",
    "landing.mobile.desc": "Clariva es una Progressive Web App — añádela a la pantalla de inicio en iOS o Android con un toque. Tu repositorio se almacena en caché para revisión sin conexión, y las notificaciones push te avisan al instante cuando un contrato espera tu aprobación.",
    "landing.mobile.feat.install.title": "Instalación en un toque",
    "landing.mobile.feat.install.desc": "Icono en la pantalla, sin tienda",
    "landing.mobile.feat.offline.title": "Listo sin conexión",
    "landing.mobile.feat.offline.desc": "Modo lectura sin señal",
    "landing.mobile.feat.push.title": "Alertas push",
    "landing.mobile.feat.push.desc": "Aprobaciones, firmas, vencimientos",
    "landing.mobile.qr.title": "Escanea para abrir",
    "landing.mobile.qr.subtitle": "Apunta la cámara al código QR",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → Compartir → Añadir a inicio",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → menú → Instalar app",
    "landing.mobile.qr.fallback": "O simplemente visita",
    "landing.testimonials.eyebrow": "Lo que dicen nuestros usuarios",
    "landing.testimonials.title": "«Reemplazamos a dos auxiliares legales y una herramienta de firma electrónica.»",
    "landing.who.eyebrow": "Quiénes somos",
    "landing.who.title": "Un equipo obsesionado con los contratos, ex fundadores de legal-tech e ingenieros de IA.",
    "landing.who.desc": "Clariva nació en Dubái con una convicción: los firmantes merecen la misma inteligencia que los redactores. Estamos construyendo la plataforma que deseamos haber tenido cuando dirigíamos el área legal de una fintech serie D.",
    "landing.contact.eyebrow": "Hablemos",
    "landing.contact.title": "Reserva una demo en vivo de 30 minutos.",
    "landing.contact.desc": "Muéstranos tu contrato más complicado y lo pasaremos por Clariva en directo. Sin diapositivas, sin grabación — solo el producto.",
    "landing.contact.form.name": "Nombre completo", "landing.contact.form.email": "Correo de trabajo",
    "landing.contact.form.company": "Empresa", "landing.contact.form.message": "¿Qué te gustaría ver?",
    "landing.contact.form.submit": "Solicitar demo",
    "landing.final.title": "Mira tu próximo contrato desde ambos lados.",
    "landing.final.desc": "Espacio gratuito, tres contratos de ejemplo, sin tarjeta de crédito.",
    "landing.footer.tagline": "La plataforma de inteligencia contractual transparente.",
    "landing.footer.product": "Producto", "landing.footer.company": "Empresa",
    "landing.footer.legal": "Legal", "landing.footer.rights": "Todos los derechos reservados.",
    "landing.pricing.eyebrow": "Planes para cada equipo",
    "landing.pricing.title": "Precios simples y transparentes.",
    "landing.pricing.subtitle": "Empieza gratis con los 9 módulos. Mejora cuando superes los límites.",
    "landing.pricing.popular": "Más popular",
    "landing.pricing.free": "Gratis", "landing.pricing.starter": "Starter",
    "landing.pricing.pro": "Profesional", "landing.pricing.enterprise": "Empresarial",
    "landing.pricing.cta.free": "Empezar gratis",
    "landing.pricing.cta.paid": "Elegir plan",
    "landing.pricing.cta.enterprise": "Hablar con ventas",
    "landing.pricing.contact": "A medida",
    "landing.pricing.billed.monthly": "por usuario / mes",
    "landing.pricing.billed.forever": "para siempre, sin tarjeta",
    "landing.pricing.billed.contact": "ajustado a tu equipo",
    "landing.pricing.unlimited": "Ilimitado",
    "landing.pricing.contracts": "contratos",
    "landing.pricing.seats": "asientos de equipo",
    "landing.pricing.fineprint": "Precios mostrados en {currency}. Convertidos desde USD a tipos indicativos — facturado en tu moneda contratada.",
    
    "landing.process.eyebrow": "El proceso Clariva",
    "landing.process.title.l1": "Del borrador a la firma,",
    "landing.process.title.l2": "en seis etapas transparentes.",
    "landing.process.subtitle": "Cada etapa instrumentada. Cada decisión de IA explicada. Cada firmante ve lo que tú ves.",
    "landing.process.s1.title": "Sube o redacta",
    "landing.process.s1.desc": "PDF, DOCX, o parte de una de las 15 plantillas sectoriales. Listo para OCR.",
    "landing.process.s2.title": "La IA disecciona cada cláusula",
    "landing.process.s2.desc": "Claude Sonnet 4.5 puntúa el riesgo de cada cláusula, la mapea a obligaciones, traduce la jerga.",
    "landing.process.s3.title": "Informe de impacto",
    "landing.process.s3.desc": "Tarjeta de puntuación en 8 ejes: financiero, operativo, reputacional y más — en segundos.",
    "landing.process.s4.title": "Resumen en audio",
    "landing.process.s4.desc": "Resumen de voz de 60 s con OpenAI TTS. Enlace compartible para directivos y contrapartes.",
    "landing.process.s5.title": "Enrutar por flujo",
    "landing.process.s5.desc": "Diseñador arrastrar y soltar: aprobaciones, ramas de decisión, revisiones paralelas, disparadores.",
    "landing.process.s6.title": "Firma con auditoría",
    "landing.process.s6.desc": "E-firma vía QR, registro infalsificable, monitor de obligaciones desde el momento de la firma.",
    "landing.walkthrough.eyebrow": "Recorrido de 2 minutos",
    "landing.walkthrough.title": "Mira a Clariva diseccionar un MSA real.",
    "landing.walkthrough.subtitle": "Mira cómo un contrato completo pasa del drop-zone a la firma en menos de dos minutos — con cada decisión de IA explicada.",
    "landing.walkthrough.duration": "2:14 · Con voz en off",
    "landing.walkthrough.kpi1.n": "2,1×",
    "landing.walkthrough.kpi1.l": "Ciclos de negociación más rápidos",
    "landing.walkthrough.kpi2.n": "68 %",
    "landing.walkthrough.kpi2.l": "Menos obligaciones perdidas",
    "landing.walkthrough.kpi3.l": "Ahorro medio el primer año",
    "landing.testimonials.t1.quote": "Nuestro abogado general pasó de leer 40 contratos a la semana a revisar los informes de impacto de Clariva en 10 minutos cada uno. Cerramos 3× más acuerdos el último trimestre.",
    "landing.testimonials.t1.name": "Maya Chen",
    "landing.testimonials.t1.role": "COO, CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "El briefing en audio por sí solo amortiza la plataforma. Mi CEO los escucha mientras va a trabajar.",
    "landing.testimonials.t2.name": "Luis Rivera",
    "landing.testimonials.t2.role": "VP Legal Ops, Harmonic Wave Records",
    "landing.testimonials.t3.quote": "Por fin una herramienta que no trata a los firmantes como algo secundario. Nuestros proveedores confían más en nosotros porque obtienen un desglose de riesgos transparente antes de firmar.",
    "landing.testimonials.t3.name": "Priya Rahman",
    "landing.testimonials.t3.role": "Fundadora, Arcadia Health Collective",
    "landing.who.title2": "Un equipo de operadores legales, investigadores de ML y diseñadores — unidos por una creencia.",
    "landing.who.desc2": "Los contratos no deberían ser armas unilaterales. Deberían ser entendimiento compartido entre dos partes, ambas con la misma claridad. Clariva nació en un despacho frustrado de ver clientes firmando cosas que no entendían. Estamos aquí para que eso sea pasado.",
    "landing.who.pillar1.title": "Transparencia primero",
    "landing.who.pillar1.desc": "Nos negamos a ocultar el razonamiento de la IA. Cada puntuación y alerta viene con una cita del documento fuente.",
    "landing.who.pillar2.title": "Los firmantes importan",
    "landing.who.pillar2.desc": "Los contratos vinculan a ambas partes. Construimos para quien firma al final, no solo para el abogado que redacta primero.",
    "landing.who.pillar3.title": "Velocidad empresarial",
    "landing.who.pillar3.desc": "Análisis de cláusulas en sub-segundo, audio en streaming, despliegues sin tiempo de inactividad. Sobre Claude 4.5 + FastAPI + React.",
    "landing.contact.eyebrow2": "Hablemos",
    "landing.contact.title2": "Reserva una demo completa, o agenda una eSession.",
    "landing.contact.desc2": "Cuéntanos sobre tu equipo — te emparejamos con un especialista de tu sector y preparamos un recorrido personalizado con tu contrato real (bajo NDA, por supuesto).",
    "landing.contact.bullet1": "Recorrido de producto personalizado de 30 min",
    "landing.contact.bullet2": "Análisis de cláusulas en vivo sobre tu contrato real",
    "landing.contact.bullet3": "Manual de implementación y despliegue",
    "landing.contact.bullet4": "Modelo ROI a medida para tu gasto legal",
    "landing.contact.intent.demo": "Demo completa",
    "landing.contact.intent.esession": "Agendar una eSession",
    "landing.contact.intent.sales": "Contactar ventas",
    "landing.contact.placeholder.name": "Juana Asesora",
    "landing.contact.placeholder.email": "juana@empresa.com",
    "landing.contact.placeholder.company": "Nombre de la empresa",
    "landing.contact.placeholder.message": "Estamos evaluando inteligencia contractual para nuestro equipo legal ops...",
    "landing.contact.field.preferred_date": "Fecha preferida",
    "landing.contact.field.preferred_time": "Hora preferida",
    "landing.contact.field.message": "¿Qué te gustaría conseguir?",
    "landing.contact.privacy": "Respondemos en 2 horas hábiles. Sin spam, nunca.",
    "landing.contact.submit.demo": "Solicitar demo completa",
    "landing.contact.submit.esession": "Reservar eSession",
    "landing.contact.submit.sales": "Contactar ventas",
    "landing.contact.submitting": "Enviando…",
    "landing.contact.success.title": "¡Listo!",
    "landing.contact.success.desc": "Te contactaremos en 2 horas hábiles con un enlace de calendario.",
    "landing.contact.toast.required": "El nombre y el email son obligatorios",
    "landing.contact.toast.success": "¡Gracias! Te contactaremos pronto.",
    "landing.contact.toast.esession": "Solicitud de eSession recibida — la confirmaremos en 2 horas hábiles",
    "landing.final.title2.l1": "Empieza a ver ambos lados",
    "landing.final.title2.l2": "de tu próximo contrato.",
    "landing.final.desc2": "Gratis para empezar. Sin tarjeta. El registro toma menos de 30 segundos y crea un workspace de demo completo.",
    "landing.final.cta.start": "Empieza gratis ya",
    "landing.final.cta.demo": "Probar demo@clariva.ai",
    "landing.footer.tagline2": "Inteligencia contractual con IA transparente. Para redactores y firmantes, por igual.",
    "landing.footer.product.items": "Funciones,Flujos,Resúmenes en audio,Impacto empresarial",
    "landing.footer.company.items": "Quiénes somos,Empleo,Kit de prensa,Seguridad",
    "landing.footer.legal.items": "Términos,Privacidad,DPA,Informe SOC 2",
    "landing.footer.copyright": "© 2026 Clariva. Inteligencia contractual con IA transparente.",
    "landing.footer.madein": "Hecho con precisión en SF · Londres · Dubái",
        "landing.pricing.feat.all_9_modules": "Los 9 módulos",
    "landing.pricing.feat.ai_contract_analysis": "Análisis contractual con IA",
    "landing.pricing.feat.business_impact_reports": "Informes de impacto",
    "landing.pricing.feat.audio_briefings_limited": "Resúmenes en audio (limitado)",
    "landing.pricing.feat.email_support": "Soporte por email",
    "landing.pricing.feat.everything_in_free": "Todo lo del plan Gratis",
    "landing.pricing.feat.everything_in_starter": "Todo lo del plan Starter",
    "landing.pricing.feat.everything_in_professional": "Todo lo del plan Profesional",
    "landing.pricing.feat.5_team_seats": "5 asientos de equipo",
    "landing.pricing.feat.25_team_seats": "25 asientos de equipo",
    "landing.pricing.feat.10_integrations": "10 integraciones",
    "landing.pricing.feat.custom_templates": "Plantillas personalizadas",
    "landing.pricing.feat.priority_email_support": "Soporte por email prioritario",
    "landing.pricing.feat.api_access": "Acceso API",
    "landing.pricing.feat.custom_rbac_roles": "Roles RBAC personalizados",
    "landing.pricing.feat.sso_google_okta": "SSO (Google, Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99,9 %",
    "landing.pricing.feat.dedicated_csm": "CSM dedicado",
    "landing.pricing.feat.cross_contract_ripple_analysis": "Análisis en cascada entre contratos",
    "landing.pricing.feat.approval_workflows": "Flujos de aprobación",
    "landing.pricing.feat.unlimited_everything": "Todo ilimitado",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "IA fine-tuned por inquilino",
    "landing.pricing.feat.byok_encryption": "Cifrado BYOK",
    "landing.pricing.feat.regional_data_residency": "Residencia regional de datos",
    "landing.pricing.feat.24_7_phone_support": "Soporte telefónico 24/7",
    "landing.pricing.feat.custom_compliance_packs": "Paquetes de cumplimiento a medida",
    "landing.pricing.feat.soc_2_type_ii_report": "Informe SOC 2 Type II",
    "landing.pricing.feat.red_team_audit": "Auditoría Red Team",
    "locale.banner.detected": "Hemos puesto tu idioma en {lang} según tu ubicación.",
    "locale.banner.undo": "Volver a inglés",
    "locale.banner.keep": "Mantener {lang}",
  },

  /* ============================ GERMAN ============================ */
  de: {
    "nav.dashboard": "Dashboard", "nav.studio": "Vertragsstudio", "nav.repository": "Repository",
    "nav.workflows": "Workflows", "nav.impact": "Geschäftsauswirkung", "nav.monitoring": "Überwachung",
    "nav.counterparty": "Gegenparteien", "nav.integrations": "Integrationen",
    "nav.analytics": "Analytik", "nav.team": "Team & Rollen", "nav.subscription": "Abonnement",
    "nav.compliance": "Compliance", "nav.settings": "Einstellungen",
    "common.save": "Speichern", "common.cancel": "Abbrechen", "common.new": "Neu",
    "common.ask": "Clariva fragen…", "common.signin": "Anmelden", "common.getstarted": "Loslegen",
    "common.bookdemo": "Live-Demo buchen", "common.startfree": "Kostenlos starten — keine Karte",
    "common.watch": "2-Min-Walkthrough ansehen", "common.theme": "Theme",
    "landing.nav.features": "Funktionen", "landing.nav.howitworks": "So funktioniert's",
    "landing.nav.industries": "Branchen", "landing.nav.mobile": "Mobile App",
        "landing.nav.product": "Produkt",
    "landing.nav.solutions": "Lösungen",
    "landing.nav.testdemo": "Demo testen",
    "landing.nav.pricing": "Preise",
    "landing.nav.who": "Über uns", "landing.nav.contact": "Kontakt",
    "landing.hero.eyebrow": "Die transparente Plattform für Vertragsintelligenz",
    "landing.hero.title": "Sehen Sie beide Seiten",
    "landing.hero.subtitle": "Clariva ist die erste KI-Vertragsplattform, die Verfasser UND Unterzeichner mit gleicher Intelligenz unterstützt — Klausel-für-Klausel-Analyse, Audio-Briefings, Auswirkungsberichte vor der Unterschrift und Drag-and-Drop-Genehmigungs-Workflows.",
    "landing.hero.badge.soc": "SOC 2 · DSGVO · HIPAA",
    "landing.hero.badge.lang": "10 Sprachen · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "Vertraut von schnellen Legal- & Ops-Teams",
    "landing.features.eyebrow": "Für Nicht-Juristen gebaut. Von Juristen geliebt.",
    "landing.features.title": "Neun Gründe, warum Teams ihre Redline-PDFs durch Clariva ersetzen.",
    "landing.feat.signatory.title": "Unterzeichner-Erlebnis",
    "landing.feat.signatory.desc": "Pro/Contra-Klauselanalyse aus IHRER Perspektive — nicht nur der des Verfassers. Endlich Transparenz beim Unterschreiben.",
    "landing.feat.audio.title": "Audio-Briefings",
    "landing.feat.audio.desc": "OpenAI TTS liest eine 60-sekündige Klartext-Zusammenfassung. Ideal für Führungskräfte, die keine Verträge lesen.",
    "landing.feat.impact.title": "Business-Impact-Berichte",
    "landing.feat.impact.desc": "Quantifizierte Pre-Signing-Bewertung in 8 Domänen: finanziell, operativ, Reputation, regulatorisch und mehr.",
    "landing.feat.workflows.title": "Drag-and-Drop-Workflows",
    "landing.feat.workflows.desc": "Visueller Genehmigungsdesigner mit Verzweigungen, Auslösern, E-Signatur und KI-Schritten.",
    "landing.feat.obligations.title": "Verpflichtungs-Monitoring",
    "landing.feat.obligations.desc": "SLAs, Fristen und Verstoßrisiko verfolgen. Ripple-Analyse über Ihr gesamtes Portfolio.",
    "landing.feat.compliance.title": "Compliance-Tresor",
    "landing.feat.compliance.desc": "15 erprobte Vorlagen: HIPAA BAA, DSGVO DPA, SaaS MSA, Krankenhaus-Einwilligung, Influencer, Music Dist und mehr.",
    "landing.feat.ai.title": "Claude Sonnet 4.5 Analyse",
    "landing.feat.ai.desc": "Spitzen-KI übersetzt Juristendeutsch in Entscheidungen, die Ihr CFO unterschreiben kann. Keine Halluzinationen, mit Zitaten.",
    "landing.feat.global.title": "Global einsatzbereit",
    "landing.feat.global.desc": "10 Sprachen, RTL-Arabisch, Multi-Currency, Daten-Residency MEA + EU + APAC ab Tag eins.",
    "landing.feat.counterparty.title": "Gegenpartei 360°",
    "landing.feat.counterparty.desc": "Beziehungsgraph, frühere Redlines, Verhandlungsverlauf. Wissen, mit wem Sie unterschreiben.",
    "landing.how.eyebrow": "Vom Einfügen zur Unterschrift in vier Schritten",
    "landing.how.title": "Wie Clariva einen Entwurf in einen unterzeichneten Deal verwandelt.",
    "landing.how.s1.title": "Einfügen oder hochladen",
    "landing.how.s1.desc": "PDF, DOCX, Klartext, sogar ein Notion-Link — Clariva nimmt es auf.",
    "landing.how.s2.title": "KI analysiert beide Seiten",
    "landing.how.s2.desc": "Claude liest jede Klausel aus Verfasser- UND Unterzeichner-Perspektive.",
    "landing.how.s3.title": "Genehmigung weiterleiten",
    "landing.how.s3.desc": "Genehmigungskette per Drag-and-Drop — Legal → Finance → Unterschrift.",
    "landing.how.s4.title": "Unterschreiben mit Vertrauen",
    "landing.how.s4.desc": "Gegenparteien sehen die Pro/Contra-Sicht + Audio-Briefing. Keine Überraschungen.",
    "landing.industries.eyebrow": "Bediente Branchen",
    "landing.industries.title": "Spezialisierte Playbooks für Branchen, die das meiste Papier unterschreiben.",
    "landing.ind.healthcare": "Gesundheitswesen", "landing.ind.healthcare.desc": "HIPAA BAA, OP-Einwilligungen, klinische Dienste.",
    "landing.ind.clinical": "Klinische Studien & Pharmaforschung", "landing.ind.clinical.desc": "Prüfarzt-Vereinbarungen Phase I–IV, informierte Einwilligung von Testpatienten, CRO-Verträge, Ethikkommissions-fähige Protokolle und Klauseln zur Offenlegung unerwünschter Ereignisse.",
    "landing.ind.insurance": "Versicherungen", "landing.ind.insurance.desc": "Anträge, Policen, Nachträge — mit Treu-und-Glauben-Scan und Schadensklausel-Extraktion.",
    "landing.ind.banking": "Banken & Kredit", "landing.ind.banking.desc": "Privatkredite, Hypotheken, Geschäftskredite — mit automatischer Raten- und Covenant-Extraktion.",
    "landing.ind.realestate": "Immobilien & Entwicklung", "landing.ind.realestate.desc": "Off-Plan-Verkäufe, Bar-Ankäufe, Treuhand und RERA-konforme Verträge.",
    "landing.ind.leasing": "Leasing & Mietverhältnis", "landing.ind.leasing.desc": "Wohn- und Gewerbemieten mit VPI-Anpassung.",
    "landing.ind.property": "Immobilienverwaltung", "landing.ind.property.desc": "Eigentümer-Verwalter-Verträge, Treuhandkonten, Leerstandszyklen.",
    "landing.ind.financial": "Finanzdienstleistungen", "landing.ind.financial.desc": "MSA, NDA, Fintech-Partnerschaften, regulatorische Compliance.",
    "landing.ind.saas": "Enterprise-SaaS", "landing.ind.saas.desc": "SaaS MSA, DPA, Sicherheitsanhänge, SOC-2-Attestierungen.",
    "landing.ind.legal": "Recht & Beratung", "landing.ind.legal.desc": "Mandatsschreiben, externe Berater, Konfliktprüfungen.",
    "landing.ind.education": "Bildung", "landing.ind.education.desc": "Lizenzen, Lieferantenverträge, Forschungsdaten-Sharing.",
    "landing.mobile.eyebrow": "Auf Mobil installieren",
    "landing.mobile.title.l1": "Per Smartphone genehmigen.",
    "landing.mobile.title.l2": "Offline-fähig. Native Anmutung.",
    "landing.mobile.desc": "Clariva ist eine Progressive Web App — fügen Sie sie mit einem Tipp zum Home-Bildschirm auf iOS oder Android hinzu. Ihr Repository wird für Offline-Reviews zwischengespeichert, und Push-Benachrichtigungen melden sich, sobald ein Vertrag auf Ihre Genehmigung wartet.",
    "landing.mobile.feat.install.title": "Installation per Tipp",
    "landing.mobile.feat.install.desc": "Home-Symbol, kein App-Store",
    "landing.mobile.feat.offline.title": "Offline-fähig",
    "landing.mobile.feat.offline.desc": "Lese-Modus ohne Signal",
    "landing.mobile.feat.push.title": "Push-Benachrichtigungen",
    "landing.mobile.feat.push.desc": "Genehmigungen, Unterschriften, Abläufe",
    "landing.mobile.qr.title": "Zum Öffnen scannen",
    "landing.mobile.qr.subtitle": "Richten Sie die Kamera auf einen QR-Code",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → Teilen → Zum Home-Bildschirm",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → Menü → App installieren",
    "landing.mobile.qr.fallback": "Oder besuchen Sie",
    "landing.testimonials.eyebrow": "Was Nutzer sagen",
    "landing.testimonials.title": "„Wir haben zwei Paralegals und ein E-Sign-Tool ersetzt.\"",
    "landing.who.eyebrow": "Über uns",
    "landing.who.title": "Ein Team von Vertrags-Enthusiasten, ehemaligen Legal-Tech-Gründern und KI-Ingenieuren.",
    "landing.who.desc": "Clariva wurde in Dubai mit einer Überzeugung gegründet: Unterzeichner verdienen die gleiche Intelligenz wie Verfasser. Wir bauen die Plattform, die wir uns gewünscht hätten, als wir Legal in einer Series-D-Fintech leiteten.",
    "landing.contact.eyebrow": "Sprechen Sie mit uns",
    "landing.contact.title": "Buchen Sie eine 30-minütige Live-Demo.",
    "landing.contact.desc": "Zeigen Sie uns Ihren kniffligsten Vertrag und wir lassen ihn live durch Clariva laufen. Keine Folien, keine Aufzeichnung — nur das Produkt.",
    "landing.contact.form.name": "Vollständiger Name", "landing.contact.form.email": "Geschäftliche E-Mail",
    "landing.contact.form.company": "Unternehmen", "landing.contact.form.message": "Was möchten Sie sehen?",
    "landing.contact.form.submit": "Demo anfragen",
    "landing.final.title": "Sehen Sie Ihren nächsten Vertrag von beiden Seiten.",
    "landing.final.desc": "Kostenloser Workspace, drei Beispielverträge, keine Kreditkarte.",
    "landing.footer.tagline": "Die transparente Plattform für Vertragsintelligenz.",
    "landing.footer.product": "Produkt", "landing.footer.company": "Unternehmen",
    "landing.footer.legal": "Rechtliches", "landing.footer.rights": "Alle Rechte vorbehalten.",
    "landing.pricing.eyebrow": "Pläne für jedes Team",
    "landing.pricing.title": "Einfache, transparente Preise.",
    "landing.pricing.subtitle": "Starten Sie kostenlos mit allen 9 Modulen. Upgraden Sie, wenn Sie die Limits sprengen.",
    "landing.pricing.popular": "Am beliebtesten",
    "landing.pricing.free": "Kostenlos", "landing.pricing.starter": "Starter",
    "landing.pricing.pro": "Professional", "landing.pricing.enterprise": "Enterprise",
    "landing.pricing.cta.free": "Kostenlos starten",
    "landing.pricing.cta.paid": "Plan wählen",
    "landing.pricing.cta.enterprise": "Mit Vertrieb sprechen",
    "landing.pricing.contact": "Individuell",
    "landing.pricing.billed.monthly": "pro Nutzer / Monat",
    "landing.pricing.billed.forever": "dauerhaft, ohne Karte",
    "landing.pricing.billed.contact": "passend zu Ihrem Team",
    "landing.pricing.unlimited": "Unbegrenzt",
    "landing.pricing.contracts": "Verträge",
    "landing.pricing.seats": "Team-Sitze",
    "landing.pricing.fineprint": "Preise in {currency}. Aus USD zu indikativen Kursen umgerechnet — Abrechnung in Ihrer vertraglich vereinbarten Währung.",
    
    "landing.process.eyebrow": "Der Clariva-Prozess",
    "landing.process.title.l1": "Vom Entwurf zur Unterschrift,",
    "landing.process.title.l2": "in sechs transparenten Schritten.",
    "landing.process.subtitle": "Jede Phase instrumentiert. Jede KI-Entscheidung erklärt. Jeder Unterzeichner sieht, was Sie sehen.",
    "landing.process.s1.title": "Hochladen oder entwerfen",
    "landing.process.s1.desc": "PDF, DOCX, oder starten Sie aus einer von 15 Branchenvorlagen. OCR-bereit.",
    "landing.process.s2.title": "KI seziert jede Klausel",
    "landing.process.s2.desc": "Claude Sonnet 4.5 bewertet jede Klausel auf Risiko, ordnet sie Verpflichtungen zu, übersetzt Juristendeutsch.",
    "landing.process.s3.title": "Business-Impact-Bericht",
    "landing.process.s3.desc": "Bewertungskarte über 8 Domänen: finanziell, operativ, Reputation und mehr — in Sekunden.",
    "landing.process.s4.title": "Audio-Briefing",
    "landing.process.s4.desc": "60-sekündige Sprachzusammenfassung via OpenAI TTS. Teilbarer Link für Führungskräfte und Gegenparteien.",
    "landing.process.s5.title": "Per Workflow weiterleiten",
    "landing.process.s5.desc": "Drag-and-Drop-Designer: Genehmigungen, Entscheidungs-Branches, parallele Reviews, Auto-Trigger.",
    "landing.process.s6.title": "Unterschreiben mit Audit-Trail",
    "landing.process.s6.desc": "QR-teilbare E-Signatur, manipulationssicheres Log, Pflichtenüberwachung ab Unterzeichnung.",
    "landing.walkthrough.eyebrow": "2-Minuten-Walkthrough",
    "landing.walkthrough.title": "Sehen Sie, wie Clariva einen echten MSA seziert.",
    "landing.walkthrough.subtitle": "Verfolgen Sie einen kompletten Vertrag von der Drop-Zone bis zur Unterschrift in unter zwei Minuten — mit erklärten KI-Entscheidungen.",
    "landing.walkthrough.duration": "2:14 · Mit Voice-Over",
    "landing.walkthrough.kpi1.n": "2,1×",
    "landing.walkthrough.kpi1.l": "Schnellere Verhandlungszyklen",
    "landing.walkthrough.kpi2.n": "68 %",
    "landing.walkthrough.kpi2.l": "Weniger verpasste Pflichten",
    "landing.walkthrough.kpi3.l": "Durchschnittliche Einsparungen Jahr 1",
    "landing.testimonials.t1.quote": "Unser Chefjustiziar liest statt 40 Verträgen pro Woche jetzt nur die Clariva-Impact-Berichte in je 10 Minuten. Wir haben letztes Quartal 3× mehr Deals abgeschlossen.",
    "landing.testimonials.t1.name": "Maya Chen",
    "landing.testimonials.t1.role": "COO, CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "Allein das Audio-Briefing rechnet die Plattform schon. Meine CEO hört sie auf dem Arbeitsweg.",
    "landing.testimonials.t2.name": "Luis Rivera",
    "landing.testimonials.t2.role": "VP Legal Ops, Harmonic Wave Records",
    "landing.testimonials.t3.quote": "Endlich ein Tool, das Unterzeichner ernst nimmt. Unsere Lieferanten vertrauen uns mehr, weil sie vor der Unterschrift eine transparente Risikoanalyse erhalten.",
    "landing.testimonials.t3.name": "Priya Rahman",
    "landing.testimonials.t3.role": "Gründerin, Arcadia Health Collective",
    "landing.who.title2": "Ein Team aus Legal-Operatoren, ML-Forschern und Designern — vereint durch eine Überzeugung.",
    "landing.who.desc2": "Verträge sollten keine einseitigen Waffen sein. Sie sollten ein gemeinsames Verständnis zwischen zwei Parteien sein, beide mit derselben Klarheit ausgestattet. Clariva entstand in einer Kanzlei, die genug davon hatte, Mandanten Dinge unterschreiben zu sehen, die sie nicht verstanden. Wir sind hier, um das zu beenden.",
    "landing.who.pillar1.title": "Transparenz zuerst",
    "landing.who.pillar1.desc": "Wir verstecken keine KI-Begründungen. Jede Bewertung, jede Risikomarkierung kommt mit Quellenzitat aus dem Originaldokument.",
    "landing.who.pillar2.title": "Unterzeichner zählen",
    "landing.who.pillar2.desc": "Verträge binden beide Seiten. Wir bauen für den Menschen, der zuletzt unterschreibt — nicht nur für den Anwalt, der zuerst entwirft.",
    "landing.who.pillar3.title": "Geschwindigkeit auf Enterprise-Niveau",
    "landing.who.pillar3.desc": "Klauselanalyse in unter einer Sekunde, Audio-Streaming, Zero-Downtime-Deployments. Auf Claude 4.5 + FastAPI + React.",
    "landing.contact.eyebrow2": "Sprechen Sie mit uns",
    "landing.contact.title2": "Buchen Sie eine vollständige Demo, oder vereinbaren Sie eine eSession.",
    "landing.contact.desc2": "Erzählen Sie uns kurz von Ihrem Team — wir bringen Sie mit einem Branchenspezialisten zusammen und stellen einen personalisierten Walkthrough mit Ihrem echten Vertrag zusammen (unter NDA, versteht sich).",
    "landing.contact.bullet1": "30-minütiger personalisierter Produkt-Walkthrough",
    "landing.contact.bullet2": "Live-Klauselanalyse Ihres echten Vertrags",
    "landing.contact.bullet3": "Implementierungs- und Rollout-Playbook",
    "landing.contact.bullet4": "Maßgeschneidertes ROI-Modell für Ihr Legal-Spend",
    "landing.contact.intent.demo": "Vollständige Demo",
    "landing.contact.intent.esession": "eSession vereinbaren",
    "landing.contact.intent.sales": "Vertrieb kontaktieren",
    "landing.contact.placeholder.name": "Jana Justiziarin",
    "landing.contact.placeholder.email": "jana@firma.com",
    "landing.contact.placeholder.company": "Firmenname",
    "landing.contact.placeholder.message": "Wir evaluieren Vertrags-Intelligenz für unser Legal-Ops-Team...",
    "landing.contact.field.preferred_date": "Wunschdatum",
    "landing.contact.field.preferred_time": "Wunschzeit",
    "landing.contact.field.message": "Was möchten Sie erreichen?",
    "landing.contact.privacy": "Antwort innerhalb von 2 Geschäftsstunden. Niemals Spam.",
    "landing.contact.submit.demo": "Vollständige Demo anfragen",
    "landing.contact.submit.esession": "eSession buchen",
    "landing.contact.submit.sales": "Vertrieb kontaktieren",
    "landing.contact.submitting": "Senden…",
    "landing.contact.success.title": "Eingegangen.",
    "landing.contact.success.desc": "Wir melden uns innerhalb von 2 Geschäftsstunden mit einem Kalenderlink.",
    "landing.contact.toast.required": "Name und E-Mail sind Pflicht",
    "landing.contact.toast.success": "Danke! Wir melden uns in Kürze.",
    "landing.contact.toast.esession": "eSession-Anfrage erhalten — Bestätigung innerhalb von 2 Geschäftsstunden",
    "landing.final.title2.l1": "Sehen Sie beide Seiten",
    "landing.final.title2.l2": "Ihres nächsten Vertrags.",
    "landing.final.desc2": "Kostenlos starten. Keine Kreditkarte. Anmeldung in unter 30 Sekunden — vollständiger Demo-Workspace inklusive.",
    "landing.final.cta.start": "Jetzt kostenlos starten",
    "landing.final.cta.demo": "demo@clariva.ai testen",
    "landing.footer.tagline2": "Transparente KI-Vertrags-Intelligenz. Für Verfasser und Unterzeichner gleichermaßen.",
    "landing.footer.product.items": "Funktionen,Workflows,Audio-Briefings,Business Impact",
    "landing.footer.company.items": "Über uns,Karriere,Pressekit,Sicherheit",
    "landing.footer.legal.items": "AGB,Datenschutz,DPA,SOC-2-Bericht",
    "landing.footer.copyright": "© 2026 Clariva. Transparente KI-Vertrags-Intelligenz.",
    "landing.footer.madein": "Mit Präzision gefertigt in SF · London · Dubai",
        "landing.pricing.feat.all_9_modules": "Alle 9 Module",
    "landing.pricing.feat.ai_contract_analysis": "KI-Vertragsanalyse",
    "landing.pricing.feat.business_impact_reports": "Business-Impact-Berichte",
    "landing.pricing.feat.audio_briefings_limited": "Audio-Briefings (begrenzt)",
    "landing.pricing.feat.email_support": "E-Mail-Support",
    "landing.pricing.feat.everything_in_free": "Alles aus dem kostenlosen Plan",
    "landing.pricing.feat.everything_in_starter": "Alles aus dem Starter-Plan",
    "landing.pricing.feat.everything_in_professional": "Alles aus dem Professional-Plan",
    "landing.pricing.feat.5_team_seats": "5 Team-Sitze",
    "landing.pricing.feat.25_team_seats": "25 Team-Sitze",
    "landing.pricing.feat.10_integrations": "10 Integrationen",
    "landing.pricing.feat.custom_templates": "Eigene Vorlagen",
    "landing.pricing.feat.priority_email_support": "Priorisierter E-Mail-Support",
    "landing.pricing.feat.api_access": "API-Zugriff",
    "landing.pricing.feat.custom_rbac_roles": "Eigene RBAC-Rollen",
    "landing.pricing.feat.sso_google_okta": "SSO (Google, Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99,9 %",
    "landing.pricing.feat.dedicated_csm": "Dedicated CSM",
    "landing.pricing.feat.cross_contract_ripple_analysis": "Vertragsübergreifende Ripple-Analyse",
    "landing.pricing.feat.approval_workflows": "Genehmigungs-Workflows",
    "landing.pricing.feat.unlimited_everything": "Alles unbegrenzt",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "Pro Mandant fine-tuned KI",
    "landing.pricing.feat.byok_encryption": "BYOK-Verschlüsselung",
    "landing.pricing.feat.regional_data_residency": "Regionale Datenresidenz",
    "landing.pricing.feat.24_7_phone_support": "24/7-Telefonsupport",
    "landing.pricing.feat.custom_compliance_packs": "Eigene Compliance-Pakete",
    "landing.pricing.feat.soc_2_type_ii_report": "SOC-2-Type-II-Bericht",
    "landing.pricing.feat.red_team_audit": "Red-Team-Audit",
    "locale.banner.detected": "Wir haben Ihre Sprache basierend auf Ihrem Standort auf {lang} gesetzt.",
    "locale.banner.undo": "Zurück zu Englisch",
    "locale.banner.keep": "{lang} behalten",
  },

  /* ============================ JAPANESE ============================ */
  ja: {
    "nav.dashboard": "ダッシュボード", "nav.studio": "契約スタジオ", "nav.repository": "リポジトリ",
    "nav.workflows": "ワークフロー", "nav.impact": "ビジネスインパクト", "nav.monitoring": "モニタリング",
    "nav.counterparty": "取引先 360°", "nav.integrations": "連携",
    "nav.analytics": "分析", "nav.team": "チームと役割", "nav.subscription": "サブスクリプション",
    "nav.compliance": "コンプライアンス", "nav.settings": "設定",
    "common.save": "保存", "common.cancel": "キャンセル", "common.new": "新規",
    "common.ask": "Clariva に質問…", "common.signin": "ログイン", "common.getstarted": "始める",
    "common.bookdemo": "ライブデモを予約", "common.startfree": "無料で開始 — カード不要",
    "common.watch": "2 分のウォークスルー", "common.theme": "テーマ",
    "landing.nav.features": "機能", "landing.nav.howitworks": "仕組み",
    "landing.nav.industries": "業界", "landing.nav.mobile": "モバイルアプリ",
        "landing.nav.product": "プロダクト",
    "landing.nav.solutions": "ソリューション",
    "landing.nav.testdemo": "デモを試す",
    "landing.nav.pricing": "料金",
    "landing.nav.who": "私たちについて", "landing.nav.contact": "お問い合わせ",
    "landing.hero.eyebrow": "透明な契約インテリジェンス・プラットフォーム",
    "landing.hero.title": "両方の側を見る",
    "landing.hero.subtitle": "Clariva は、起草者と署名者に等しい知能を提供する初の AI 契約プラットフォームです — 条項ごとの分析、音声ブリーフィング、署名前のビジネスインパクトレポート、ドラッグ&ドロップの承認ワークフロー。",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10 言語 · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "俊敏な法務・運用チームに信頼されています",
    "landing.features.eyebrow": "非弁護士のために。法務チームに愛される。",
    "landing.features.title": "赤入れ PDF を Clariva に置き換える 9 つの理由。",
    "landing.feat.signatory.title": "署名者体験",
    "landing.feat.signatory.desc": "起草者の視点だけでなく、あなたの視点からの賛否分析。署名時の真の透明性。",
    "landing.feat.audio.title": "音声ブリーフィング",
    "landing.feat.audio.desc": "OpenAI TTS が 60 秒の平易な要約を読み上げ。契約書を読まない経営者に最適。",
    "landing.feat.impact.title": "ビジネスインパクトレポート",
    "landing.feat.impact.desc": "署名前 8 領域の定量スコア:財務、運用、評判、規制ほか。",
    "landing.feat.workflows.title": "ドラッグ&ドロップ・ワークフロー",
    "landing.feat.workflows.desc": "分岐・自動トリガー・電子署名・AI ステップを含むビジュアル承認デザイナー。",
    "landing.feat.obligations.title": "義務監視",
    "landing.feat.obligations.desc": "SLA・期限・違反リスクを追跡。ポートフォリオ全体への波及分析。",
    "landing.feat.compliance.title": "コンプライアンス保管庫",
    "landing.feat.compliance.desc": "15 の実証済みテンプレート:HIPAA BAA、GDPR DPA、SaaS MSA、病院同意、インフルエンサー、音楽配信ほか。",
    "landing.feat.ai.title": "Claude Sonnet 4.5 解析",
    "landing.feat.ai.desc": "最先端 AI が法律用語を CFO が承認できる判断に翻訳。引用付き、ハルシネーションなし。",
    "landing.feat.global.title": "グローバル対応",
    "landing.feat.global.desc": "10 言語、RTL アラビア語、マルチ通貨、初日から MEA + EU + APAC のデータ所在地に対応。",
    "landing.feat.counterparty.title": "取引先 360°",
    "landing.feat.counterparty.desc": "関係グラフ、過去の赤入れ、交渉履歴。誰と署名するか把握。",
    "landing.how.eyebrow": "貼り付けから署名まで 4 ステップ",
    "landing.how.title": "Clariva はどのように草案を契約済み案件に変えるか。",
    "landing.how.s1.title": "貼り付けまたはアップロード",
    "landing.how.s1.desc": "PDF、DOCX、プレーンテキスト、Notion リンクも — Clariva が取り込みます。",
    "landing.how.s2.title": "AI が双方を分析",
    "landing.how.s2.desc": "Claude が起草者と署名者の両視点で全条項を読みます。",
    "landing.how.s3.title": "承認ルートに乗せる",
    "landing.how.s3.desc": "承認チェーンをドラッグ&ドロップ — 法務 → 財務 → 署名。",
    "landing.how.s4.title": "自信を持って署名",
    "landing.how.s4.desc": "相手方は賛否ビュー + 音声ブリーフィングを受け取ります。サプライズなし。",
    "landing.industries.eyebrow": "対応業界",
    "landing.industries.title": "最も多くの契約に署名する業界向けの専門プレイブック。",
    "landing.ind.healthcare": "医療", "landing.ind.healthcare.desc": "HIPAA BAA、手術同意、臨床サービス。",
    "landing.ind.clinical": "臨床試験・製薬研究", "landing.ind.clinical.desc": "第 I〜IV 相試験責任医師契約、被験者の同意・登録、CRO 契約、IRB 対応プロトコル、有害事象開示条項。",
    "landing.ind.insurance": "保険会社", "landing.ind.insurance.desc": "申込書・約款・特約 — 最善の信義開示スキャンと請求条項抽出。",
    "landing.ind.banking": "銀行・融資", "landing.ind.banking.desc": "個人ローン、住宅ローン、事業融資 — 返済額・コベナンツの自動抽出。",
    "landing.ind.realestate": "不動産・開発", "landing.ind.realestate.desc": "プレオープン販売、現金購入、エスクロー、RERA 対応契約。",
    "landing.ind.leasing": "賃貸・リース", "landing.ind.leasing.desc": "CPI 連動の住宅・商業賃貸契約。",
    "landing.ind.property": "プロパティマネジメント", "landing.ind.property.desc": "オーナー・マネージャー契約、信託会計、空室サイクル。",
    "landing.ind.financial": "金融サービス", "landing.ind.financial.desc": "MSA、NDA、フィンテック提携、規制遵守。",
    "landing.ind.saas": "エンタープライズ SaaS", "landing.ind.saas.desc": "SaaS MSA、DPA、セキュリティ補遺、SOC 2 認証。",
    "landing.ind.legal": "法律・顧問", "landing.ind.legal.desc": "委任状、外部弁護士、利益相反チェック。",
    "landing.ind.education": "教育", "landing.ind.education.desc": "ライセンス、ベンダー契約、研究データ共有。",
    "landing.mobile.eyebrow": "モバイルにインストール",
    "landing.mobile.title.l1": "スマホから承認。",
    "landing.mobile.title.l2": "オフライン対応。ネイティブな感触。",
    "landing.mobile.desc": "Clariva はプログレッシブ Web アプリ — ワンタップで iOS / Android のホーム画面に追加。リポジトリはオフライン閲覧のためにキャッシュされ、プッシュ通知が承認待ちの契約を即座に知らせます。",
    "landing.mobile.feat.install.title": "ワンタップ・インストール",
    "landing.mobile.feat.install.desc": "ホーム画面アイコン、ストア不要",
    "landing.mobile.feat.offline.title": "オフライン対応",
    "landing.mobile.feat.offline.desc": "電波なしで閲覧モード",
    "landing.mobile.feat.push.title": "プッシュ通知",
    "landing.mobile.feat.push.desc": "承認・署名・期限",
    "landing.mobile.qr.title": "スキャンして開く",
    "landing.mobile.qr.subtitle": "カメラを QR コードに向けてください",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → 共有 → ホーム画面に追加",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → メニュー → アプリをインストール",
    "landing.mobile.qr.fallback": "またはこちらにアクセス",
    "landing.testimonials.eyebrow": "ユーザーの声",
    "landing.testimonials.title": "「パラリーガル 2 名と電子署名ツールを置き換えました。」",
    "landing.who.eyebrow": "私たちについて",
    "landing.who.title": "契約に取り憑かれたチーム、元 Legal-Tech 創業者と AI エンジニア。",
    "landing.who.desc": "Clariva はドバイで、ある信念とともに創業されました:署名者は起草者と同じ知能を得るに値する。私たちはシリーズ D フィンテックで法務を率いていた頃に欲しかったプラットフォームを作っています。",
    "landing.contact.eyebrow": "お話ししましょう",
    "landing.contact.title": "30 分のライブデモを予約。",
    "landing.contact.desc": "最も難しい契約書を見せてください。Clariva にライブで通します。スライドも録画もなく、製品のみ。",
    "landing.contact.form.name": "氏名", "landing.contact.form.email": "業務用メール",
    "landing.contact.form.company": "会社", "landing.contact.form.message": "何をご覧になりたいですか?",
    "landing.contact.form.submit": "デモをリクエスト",
    "landing.final.title": "次の契約を両側から見る。",
    "landing.final.desc": "無料ワークスペース、サンプル契約 3 件、クレジットカード不要。",
    "landing.footer.tagline": "透明な契約インテリジェンス・プラットフォーム。",
    "landing.footer.product": "製品", "landing.footer.company": "会社",
    "landing.footer.legal": "法的事項", "landing.footer.rights": "全著作権所有。",
    "landing.pricing.eyebrow": "あらゆるチーム向けプラン",
    "landing.pricing.title": "シンプルで透明な料金体系。",
    "landing.pricing.subtitle": "9 モジュールすべてを無料で開始。上限に達したらアップグレード。",
    "landing.pricing.popular": "最も人気",
    "landing.pricing.free": "無料", "landing.pricing.starter": "スターター",
    "landing.pricing.pro": "プロフェッショナル", "landing.pricing.enterprise": "エンタープライズ",
    "landing.pricing.cta.free": "無料で開始",
    "landing.pricing.cta.paid": "このプランを選ぶ",
    "landing.pricing.cta.enterprise": "営業に相談",
    "landing.pricing.contact": "カスタム",
    "landing.pricing.billed.monthly": "ユーザー / 月",
    "landing.pricing.billed.forever": "永久無料、カード不要",
    "landing.pricing.billed.contact": "チームに合わせて",
    "landing.pricing.unlimited": "無制限",
    "landing.pricing.contracts": "件の契約",
    "landing.pricing.seats": "チームシート",
    "landing.pricing.fineprint": "料金は {currency} 表示。USD から参考レートで換算 — ご契約通貨で請求します。",
    
    "landing.process.eyebrow": "Clariva のプロセス",
    "landing.process.title.l1": "草案から署名まで、",
    "landing.process.title.l2": "6 つの透明な段階で。",
    "landing.process.subtitle": "各段階を計測。すべての AI 判断を説明。すべての署名者があなたと同じ画面を見る。",
    "landing.process.s1.title": "アップロードまたは起草",
    "landing.process.s1.desc": "PDF、DOCX、または 15 業界テンプレートの一つから開始。OCR 対応。",
    "landing.process.s2.title": "AI が全条項を解析",
    "landing.process.s2.desc": "Claude Sonnet 4.5 が各条項のリスクを採点、義務にマッピング、専門用語を翻訳。",
    "landing.process.s3.title": "ビジネスインパクトレポート",
    "landing.process.s3.desc": "8 領域の評価カード:財務、運用、評判ほか — 数秒で。",
    "landing.process.s4.title": "音声ブリーフィング",
    "landing.process.s4.desc": "OpenAI TTS による 60 秒の音声要約。役員や相手方と共有可能なリンク。",
    "landing.process.s5.title": "ワークフローでルーティング",
    "landing.process.s5.desc": "ドラッグ&ドロップ デザイナー:承認、判断分岐、並列レビュー、自動トリガー。",
    "landing.process.s6.title": "監査証跡付き署名",
    "landing.process.s6.desc": "QR で共有可能な電子署名、改ざん不可ログ、署名後すぐに義務監視を起動。",
    "landing.walkthrough.eyebrow": "2 分のウォークスルー",
    "landing.walkthrough.title": "Clariva が実際の MSA を解析する様子を見る。",
    "landing.walkthrough.subtitle": "投入から署名までの完全な契約を 2 分以内で——すべての AI 判断を解説。",
    "landing.walkthrough.duration": "2:14 · ナレーション付き",
    "landing.walkthrough.kpi1.n": "2.1×",
    "landing.walkthrough.kpi1.l": "交渉サイクルの高速化",
    "landing.walkthrough.kpi2.n": "68%",
    "landing.walkthrough.kpi2.l": "見落とした義務の削減",
    "landing.walkthrough.kpi3.l": "初年度平均削減額",
    "landing.testimonials.t1.quote": "当社の法務責任者は週 40 件の契約を読む生活から、Clariva のインパクトレポートを各 10 分でレビューする生活へ。前四半期は 3 倍の案件を成立させました。",
    "landing.testimonials.t1.name": "マヤ・チェン",
    "landing.testimonials.t1.role": "COO、CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "音声ブリーフィング機能だけでプラットフォーム代を回収できます。私の CEO は通勤中に聞いています。",
    "landing.testimonials.t2.name": "ルイス・リベラ",
    "landing.testimonials.t2.role": "VP Legal Ops、Harmonic Wave Records",
    "landing.testimonials.t3.quote": "署名者を後付けで扱わない初めてのツール。署名前に透明なリスク内訳が得られるため、ベンダーの信頼が増しました。",
    "landing.testimonials.t3.name": "プリヤ・ラフマン",
    "landing.testimonials.t3.role": "創業者、Arcadia Health Collective",
    "landing.who.title2": "法務オペレーター、ML 研究者、デザイナーのチーム ——一つの信念で団結。",
    "landing.who.desc2": "契約は一方的な武器であってはならない。双方が同じ明瞭さを持って共有する理解であるべき。Clariva は、顧客が理解しないまま署名する姿を見るのにうんざりした法律事務所内で生まれました。それを過去のものにします。",
    "landing.who.pillar1.title": "透明性が第一",
    "landing.who.pillar1.desc": "AI の推論を隠しません。すべてのスコアとリスク警告は、ソース文書からの引用付き。",
    "landing.who.pillar2.title": "署名者が大事",
    "landing.who.pillar2.desc": "契約は両者を拘束します。最初に起草する弁護士だけでなく、最後に署名する人間のために設計します。",
    "landing.who.pillar3.title": "エンタープライズ級の速度",
    "landing.who.pillar3.desc": "1 秒未満の条項解析、ストリーミング音声、ダウンタイムなしのデプロイ。Claude 4.5 + FastAPI + React 上に構築。",
    "landing.contact.eyebrow2": "ご相談ください",
    "landing.contact.title2": "フルデモを予約、または eSession を手配。",
    "landing.contact.desc2": "チームについて少し教えてください。あなたの業界の専門家とマッチングし、実際の契約書(NDA 締結のうえ)で個別ウォークスルーをご用意します。",
    "landing.contact.bullet1": "30 分の個別製品ウォークスルー",
    "landing.contact.bullet2": "実契約のライブ条項解析",
    "landing.contact.bullet3": "実装・展開プレイブック",
    "landing.contact.bullet4": "法務支出向けカスタム ROI モデル",
    "landing.contact.intent.demo": "フルデモ",
    "landing.contact.intent.esession": "eSession を手配",
    "landing.contact.intent.sales": "営業に連絡",
    "landing.contact.placeholder.name": "山田 太郎",
    "landing.contact.placeholder.email": "taro@company.com",
    "landing.contact.placeholder.company": "会社名",
    "landing.contact.placeholder.message": "法務オペレーション向けの契約インテリジェンスを評価中…",
    "landing.contact.field.preferred_date": "希望日",
    "landing.contact.field.preferred_time": "希望時刻",
    "landing.contact.field.message": "達成したいことは?",
    "landing.contact.privacy": "営業時間内 2 時間以内にご返信。スパム送信は一切ありません。",
    "landing.contact.submit.demo": "フルデモをリクエスト",
    "landing.contact.submit.esession": "eSession を予約",
    "landing.contact.submit.sales": "営業に連絡",
    "landing.contact.submitting": "送信中…",
    "landing.contact.success.title": "受け付けました。",
    "landing.contact.success.desc": "営業時間内 2 時間以内にカレンダーリンクをお送りします。",
    "landing.contact.toast.required": "名前とメールは必須です",
    "landing.contact.toast.success": "ありがとうございます!まもなくご連絡します。",
    "landing.contact.toast.esession": "eSession のリクエストを受け付けました — 営業時間内 2 時間以内に確認します",
    "landing.final.title2.l1": "次の契約を",
    "landing.final.title2.l2": "両側から見始めましょう。",
    "landing.final.desc2": "無料で開始。クレジットカード不要。30 秒未満で登録、フルデモワークスペースが付属。",
    "landing.final.cta.start": "今すぐ無料で開始",
    "landing.final.cta.demo": "demo@clariva.ai を試す",
    "landing.footer.tagline2": "透明な AI 契約インテリジェンス。起草者にも署名者にも平等に。",
    "landing.footer.product.items": "機能,ワークフロー,音声ブリーフィング,ビジネスインパクト",
    "landing.footer.company.items": "私たちについて,採用,プレスキット,セキュリティ",
    "landing.footer.legal.items": "利用規約,プライバシー,DPA,SOC 2 レポート",
    "landing.footer.copyright": "© 2026 Clariva. 透明な AI 契約インテリジェンス。",
    "landing.footer.madein": "SF · ロンドン · ドバイで丹精込めて",
        "landing.pricing.feat.all_9_modules": "全 9 モジュール",
    "landing.pricing.feat.ai_contract_analysis": "AI 契約解析",
    "landing.pricing.feat.business_impact_reports": "ビジネスインパクトレポート",
    "landing.pricing.feat.audio_briefings_limited": "音声ブリーフィング(制限あり)",
    "landing.pricing.feat.email_support": "メールサポート",
    "landing.pricing.feat.everything_in_free": "無料プランのすべて",
    "landing.pricing.feat.everything_in_starter": "スタータープランのすべて",
    "landing.pricing.feat.everything_in_professional": "プロフェッショナルプランのすべて",
    "landing.pricing.feat.5_team_seats": "5 チームシート",
    "landing.pricing.feat.25_team_seats": "25 チームシート",
    "landing.pricing.feat.10_integrations": "10 連携",
    "landing.pricing.feat.custom_templates": "カスタムテンプレート",
    "landing.pricing.feat.priority_email_support": "優先メールサポート",
    "landing.pricing.feat.api_access": "API アクセス",
    "landing.pricing.feat.custom_rbac_roles": "カスタム RBAC ロール",
    "landing.pricing.feat.sso_google_okta": "SSO (Google、Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99.9%",
    "landing.pricing.feat.dedicated_csm": "専任 CSM",
    "landing.pricing.feat.cross_contract_ripple_analysis": "契約横断のリップル解析",
    "landing.pricing.feat.approval_workflows": "承認ワークフロー",
    "landing.pricing.feat.unlimited_everything": "すべて無制限",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "テナントごとにファインチューニング",
    "landing.pricing.feat.byok_encryption": "BYOK 暗号化",
    "landing.pricing.feat.regional_data_residency": "地域別データ所在",
    "landing.pricing.feat.24_7_phone_support": "24/7 電話サポート",
    "landing.pricing.feat.custom_compliance_packs": "カスタムコンプライアンスパック",
    "landing.pricing.feat.soc_2_type_ii_report": "SOC 2 Type II レポート",
    "landing.pricing.feat.red_team_audit": "レッドチーム監査",
    "locale.banner.detected": "ご利用地域に基づいて言語を {lang} に設定しました。",
    "locale.banner.undo": "英語に戻す",
    "locale.banner.keep": "{lang} のまま",
  },

  /* ============================ MANDARIN (Simplified) ============================ */
  zh: {
    "nav.dashboard": "仪表盘", "nav.studio": "合同工作室", "nav.repository": "合同库",
    "nav.workflows": "审批流程", "nav.impact": "业务影响", "nav.monitoring": "监控",
    "nav.counterparty": "对手方 360°", "nav.integrations": "集成",
    "nav.analytics": "分析", "nav.team": "团队与角色", "nav.subscription": "订阅",
    "nav.compliance": "合规", "nav.settings": "设置",
    "common.save": "保存", "common.cancel": "取消", "common.new": "新建",
    "common.ask": "向 Clariva 提问…", "common.signin": "登录", "common.getstarted": "开始使用",
    "common.bookdemo": "预约现场演示", "common.startfree": "免费开始 — 无需信用卡",
    "common.watch": "观看 2 分钟演示", "common.theme": "主题",
    "landing.nav.features": "功能", "landing.nav.howitworks": "工作原理",
    "landing.nav.industries": "行业", "landing.nav.mobile": "移动应用",
        "landing.nav.product": "产品",
    "landing.nav.solutions": "解决方案",
    "landing.nav.testdemo": "试用演示",
    "landing.nav.pricing": "价格",
    "landing.nav.who": "关于我们", "landing.nav.contact": "联系我们",
    "landing.hero.eyebrow": "透明的合同智能平台",
    "landing.hero.title": "看到双方视角",
    "landing.hero.subtitle": "Clariva 是首个为起草方和签署方提供同等智能服务的 AI 合同平台 — 逐条款分析、音频简报、签字前业务影响报告以及拖放式审批流程。",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10 种语言 · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "受到敏捷法务与运营团队的信赖",
    "landing.features.eyebrow": "为非律师打造,法务团队挚爱。",
    "landing.features.title": "团队用 Clariva 取代红线 PDF 的九大理由。",
    "landing.feat.signatory.title": "签署方体验",
    "landing.feat.signatory.desc": "从你的视角而非仅起草方视角的赞同/反对条款分析。终于在签字时拥有真正透明度。",
    "landing.feat.audio.title": "音频简报",
    "landing.feat.audio.desc": "OpenAI TTS 朗读 60 秒通俗摘要。最适合不读合同的高管。",
    "landing.feat.impact.title": "业务影响报告",
    "landing.feat.impact.desc": "签字前 8 个维度量化评分:财务、运营、声誉、合规等。",
    "landing.feat.workflows.title": "拖放式工作流",
    "landing.feat.workflows.desc": "可视化审批设计器,支持分支、触发器、电子签名与 AI 步骤。",
    "landing.feat.obligations.title": "义务监控",
    "landing.feat.obligations.desc": "追踪 SLA、截止日期与违约风险。在整个合同组合上进行连锁分析。",
    "landing.feat.compliance.title": "合规保险库",
    "landing.feat.compliance.desc": "15 个经过实战检验的模板:HIPAA BAA、GDPR DPA、SaaS MSA、医院同意书、网红合作、音乐发行等。",
    "landing.feat.ai.title": "Claude Sonnet 4.5 分析",
    "landing.feat.ai.desc": "前沿 AI 把法律术语翻译成 CFO 可以拍板的决策。无幻觉,带引用。",
    "landing.feat.global.title": "全球就绪",
    "landing.feat.global.desc": "10 种语言、阿拉伯语 RTL、多币种,首日即支持中东、欧洲、亚太数据驻留。",
    "landing.feat.counterparty.title": "对手方 360°",
    "landing.feat.counterparty.desc": "关系图谱、过往红线、谈判历史。清楚知道你在与谁签约。",
    "landing.how.eyebrow": "从粘贴到签字四步走",
    "landing.how.title": "Clariva 如何把草稿变成已签合约。",
    "landing.how.s1.title": "粘贴或上传",
    "landing.how.s1.desc": "PDF、DOCX、纯文本,甚至 Notion 链接 — Clariva 都能识别。",
    "landing.how.s2.title": "AI 双向分析",
    "landing.how.s2.desc": "Claude 从起草方与签署方双重视角阅读每一个条款。",
    "landing.how.s3.title": "进入审批流",
    "landing.how.s3.desc": "拖放审批链 — 法务 → 财务 → 签字。",
    "landing.how.s4.title": "自信签字",
    "landing.how.s4.desc": "对手方获得赞同/反对视图 + 音频简报。绝无意外。",
    "landing.industries.eyebrow": "服务行业",
    "landing.industries.title": "为签署最多文件的行业打造的专属操作手册。",
    "landing.ind.healthcare": "医疗", "landing.ind.healthcare.desc": "HIPAA BAA、手术同意、临床服务。",
    "landing.ind.clinical": "临床试验与制药研究", "landing.ind.clinical.desc": "I–IV 期研究者协议、受试患者知情同意与入组、CRO 合同、伦理委员会就绪方案,以及不良事件披露条款。",
    "landing.ind.insurance": "保险公司", "landing.ind.insurance.desc": "投保书、保单条款、批单 — 含最大诚信披露扫描与理赔条款抽取。",
    "landing.ind.banking": "银行与贷款", "landing.ind.banking.desc": "个人贷款、房贷、企业信贷 — 自动抽取等额本息与契约条款。",
    "landing.ind.realestate": "房地产与开发", "landing.ind.realestate.desc": "楼花预售、现金购房、托管与 RERA 合规协议。",
    "landing.ind.leasing": "租赁", "landing.ind.leasing.desc": "住宅与商业租赁,含 CPI 调价。",
    "landing.ind.property": "物业管理", "landing.ind.property.desc": "业主-管理方协议、信托账户、空置周期流程。",
    "landing.ind.financial": "金融服务", "landing.ind.financial.desc": "MSA、NDA、金融科技合作、监管合规。",
    "landing.ind.saas": "企业 SaaS", "landing.ind.saas.desc": "SaaS MSA、DPA、安全附录、SOC 2 认证。",
    "landing.ind.legal": "法律与顾问", "landing.ind.legal.desc": "委托函、外部律师、利益冲突检查。",
    "landing.ind.education": "教育", "landing.ind.education.desc": "授权、供应商协议、研究数据共享。",
    "landing.mobile.eyebrow": "在移动端安装",
    "landing.mobile.title.l1": "用手机审批。",
    "landing.mobile.title.l2": "支持离线。原生体验。",
    "landing.mobile.desc": "Clariva 是渐进式 Web 应用 — 在 iOS 或 Android 上一键添加到主屏。合同库会缓存以供离线查看,推送通知会在合同等待你审批的瞬间提醒你。",
    "landing.mobile.feat.install.title": "一键安装",
    "landing.mobile.feat.install.desc": "主屏图标,无需应用商店",
    "landing.mobile.feat.offline.title": "离线就绪",
    "landing.mobile.feat.offline.desc": "无信号也能阅读",
    "landing.mobile.feat.push.title": "推送提醒",
    "landing.mobile.feat.push.desc": "审批、签字、到期",
    "landing.mobile.qr.title": "扫码打开",
    "landing.mobile.qr.subtitle": "用手机相机对准二维码",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → 分享 → 添加到主屏",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → 菜单 → 安装应用",
    "landing.mobile.qr.fallback": "或直接访问",
    "landing.testimonials.eyebrow": "用户的反馈",
    "landing.testimonials.title": "「我们替换掉了两名律师助理和一个电子签名工具。」",
    "landing.who.eyebrow": "关于我们",
    "landing.who.title": "一群对合同入迷的人:前法务科技创始人与 AI 工程师。",
    "landing.who.desc": "Clariva 创立于迪拜,我们坚信:签署方应当与起草方拥有同等的智能。我们正在打造一个我们当年在 D 轮金融科技公司管法务时所渴望拥有的合同平台。",
    "landing.contact.eyebrow": "与我们交谈",
    "landing.contact.title": "预约 30 分钟现场演示。",
    "landing.contact.desc": "把你最棘手的合同发给我们,我们会在 Clariva 中现场跑给你看。没有幻灯片、没有录音 — 只有产品。",
    "landing.contact.form.name": "姓名", "landing.contact.form.email": "工作邮箱",
    "landing.contact.form.company": "公司", "landing.contact.form.message": "你想看到什么?",
    "landing.contact.form.submit": "申请演示",
    "landing.final.title": "从双方视角看你的下一份合同。",
    "landing.final.desc": "免费工作空间、3 份样本合同、无需信用卡。",
    "landing.footer.tagline": "透明的合同智能平台。",
    "landing.footer.product": "产品", "landing.footer.company": "公司",
    "landing.footer.legal": "法律", "landing.footer.rights": "保留所有权利。",
    "landing.pricing.eyebrow": "适合每个团队的方案",
    "landing.pricing.title": "简单、透明的定价。",
    "landing.pricing.subtitle": "9 大模块免费起步。超出额度时再升级。",
    "landing.pricing.popular": "最受欢迎",
    "landing.pricing.free": "免费", "landing.pricing.starter": "入门版",
    "landing.pricing.pro": "专业版", "landing.pricing.enterprise": "企业版",
    "landing.pricing.cta.free": "免费开始",
    "landing.pricing.cta.paid": "选择此方案",
    "landing.pricing.cta.enterprise": "联系销售",
    "landing.pricing.contact": "定制",
    "landing.pricing.billed.monthly": "每用户/每月",
    "landing.pricing.billed.forever": "永久免费,无需信用卡",
    "landing.pricing.billed.contact": "为团队量身定制",
    "landing.pricing.unlimited": "无限制",
    "landing.pricing.contracts": "份合同",
    "landing.pricing.seats": "团队席位",
    "landing.pricing.fineprint": "价格以 {currency} 显示。按参考汇率从美元换算 — 按您合同币种结算。",
    
    "landing.process.eyebrow": "Clariva 流程",
    "landing.process.title.l1": "从草稿到签字,",
    "landing.process.title.l2": "六个透明阶段。",
    "landing.process.subtitle": "每个阶段都被记录,每一项 AI 决策都被解释,每一位签署人看到的与你看到的相同。",
    "landing.process.s1.title": "上传或起草",
    "landing.process.s1.desc": "PDF、DOCX,或从 15 个行业模板之一开始。即开即用 OCR。",
    "landing.process.s2.title": "AI 解剖每一条款",
    "landing.process.s2.desc": "Claude Sonnet 4.5 对每一条款进行风险评分、对应义务、翻译法律术语。",
    "landing.process.s3.title": "业务影响报告",
    "landing.process.s3.desc": "8 大维度评分卡:财务、运营、声誉等 — 几秒钟交付。",
    "landing.process.s4.title": "音频简报",
    "landing.process.s4.desc": "OpenAI TTS 60 秒语音摘要。可分享给高管与对方的链接。",
    "landing.process.s5.title": "通过工作流路由",
    "landing.process.s5.desc": "拖放式设计器:审批、决策分支、并行评审、自动触发器。",
    "landing.process.s6.title": "签字并留存审计轨迹",
    "landing.process.s6.desc": "可通过 QR 码分享的电子签名、防篡改日志、签字后即刻启动义务监控。",
    "landing.walkthrough.eyebrow": "2 分钟演示",
    "landing.walkthrough.title": "看 Clariva 解剖一份真实的 MSA。",
    "landing.walkthrough.subtitle": "看一份完整合同从投放区到签字,只需两分钟 — 每个 AI 决策都解释清楚。",
    "landing.walkthrough.duration": "2:14 · 含语音解说",
    "landing.walkthrough.kpi1.n": "2.1×",
    "landing.walkthrough.kpi1.l": "更快的谈判周期",
    "landing.walkthrough.kpi2.n": "68%",
    "landing.walkthrough.kpi2.l": "更少错过的义务",
    "landing.walkthrough.kpi3.l": "首年平均节省",
    "landing.testimonials.t1.quote": "我们的法务总监从每周读 40 份合同,变成每份花 10 分钟看 Clariva 的影响报告。上一季度成单数 3 倍提升。",
    "landing.testimonials.t1.name": "陈玛雅",
    "landing.testimonials.t1.role": "COO,CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "光是音频简报就值回平台费用。我的 CEO 通勤路上就在听。",
    "landing.testimonials.t2.name": "路易斯·里维拉",
    "landing.testimonials.t2.role": "VP Legal Ops,Harmonic Wave Records",
    "landing.testimonials.t3.quote": "终于有一款不把签署人当附属品的工具。供应商更信任我们了,因为他们在签字前能看到透明的风险拆解。",
    "landing.testimonials.t3.name": "普利娅·拉赫曼",
    "landing.testimonials.t3.role": "创始人,Arcadia Health Collective",
    "landing.who.title2": "一支由法务运营、机器学习研究者和设计师组成的团队 — 因一个信念团结。",
    "landing.who.desc2": "合同不该是单边武器。它应是双方共同理解的产物,双方拥有同等的清晰度。Clariva 诞生于一家律所,因为我们厌倦了看着客户签署他们看不懂的东西。我们要让那一切成为过去。",
    "landing.who.pillar1.title": "透明优先",
    "landing.who.pillar1.desc": "我们拒绝隐藏 AI 推理。每一项评分、每一个风险标记都附带源文档引用。",
    "landing.who.pillar2.title": "签署人很重要",
    "landing.who.pillar2.desc": "合同约束双方。我们为最后签字的人设计,而不仅仅为最先起草的律师。",
    "landing.who.pillar3.title": "企业级速度",
    "landing.who.pillar3.desc": "亚秒级条款分析、流式音频、零停机部署。基于 Claude 4.5 + FastAPI + React。",
    "landing.contact.eyebrow2": "聊聊吧",
    "landing.contact.title2": "预约完整演示,或安排 eSession。",
    "landing.contact.desc2": "告诉我们一些团队信息 — 我们会为你匹配你所在行业的专家,并用你真实的合同(当然在 NDA 下)准备一次个性化演示。",
    "landing.contact.bullet1": "30 分钟个性化产品演示",
    "landing.contact.bullet2": "在你真实合同上的现场条款分析",
    "landing.contact.bullet3": "实施和落地手册",
    "landing.contact.bullet4": "针对你法务支出的定制 ROI 模型",
    "landing.contact.intent.demo": "完整演示",
    "landing.contact.intent.esession": "安排 eSession",
    "landing.contact.intent.sales": "联系销售",
    "landing.contact.placeholder.name": "李法务",
    "landing.contact.placeholder.email": "li@company.com",
    "landing.contact.placeholder.company": "公司名称",
    "landing.contact.placeholder.message": "我们正在评估面向法务运营团队的合同智能...",
    "landing.contact.field.preferred_date": "首选日期",
    "landing.contact.field.preferred_time": "首选时间",
    "landing.contact.field.message": "你希望实现什么?",
    "landing.contact.privacy": "工作时间 2 小时内回复。绝不发垃圾邮件。",
    "landing.contact.submit.demo": "申请完整演示",
    "landing.contact.submit.esession": "预约 eSession",
    "landing.contact.submit.sales": "联系销售",
    "landing.contact.submitting": "发送中…",
    "landing.contact.success.title": "已收到。",
    "landing.contact.success.desc": "我们将在工作时间 2 小时内通过日历链接联系你。",
    "landing.contact.toast.required": "姓名和邮箱必填",
    "landing.contact.toast.success": "感谢!我们会尽快联系。",
    "landing.contact.toast.esession": "已收到 eSession 请求 — 工作时间 2 小时内确认",
    "landing.final.title2.l1": "从两个视角看",
    "landing.final.title2.l2": "你的下一份合同。",
    "landing.final.desc2": "免费起步。无需信用卡。注册不到 30 秒,自带完整演示工作区。",
    "landing.final.cta.start": "立即免费开始",
    "landing.final.cta.demo": "试用 demo@clariva.ai",
    "landing.footer.tagline2": "透明的 AI 合同智能。起草者与签署者并重。",
    "landing.footer.product.items": "功能,工作流,音频简报,业务影响",
    "landing.footer.company.items": "关于我们,招聘,媒体资源,安全",
    "landing.footer.legal.items": "服务条款,隐私,DPA,SOC 2 报告",
    "landing.footer.copyright": "© 2026 Clariva. 透明的 AI 合同智能。",
    "landing.footer.madein": "于旧金山 · 伦敦 · 迪拜精心打造",
        "landing.pricing.feat.all_9_modules": "全部 9 个模块",
    "landing.pricing.feat.ai_contract_analysis": "AI 合同分析",
    "landing.pricing.feat.business_impact_reports": "业务影响报告",
    "landing.pricing.feat.audio_briefings_limited": "音频简报(有限)",
    "landing.pricing.feat.email_support": "邮件支持",
    "landing.pricing.feat.everything_in_free": "免费版的一切",
    "landing.pricing.feat.everything_in_starter": "入门版的一切",
    "landing.pricing.feat.everything_in_professional": "专业版的一切",
    "landing.pricing.feat.5_team_seats": "5 个团队席位",
    "landing.pricing.feat.25_team_seats": "25 个团队席位",
    "landing.pricing.feat.10_integrations": "10 个集成",
    "landing.pricing.feat.custom_templates": "自定义模板",
    "landing.pricing.feat.priority_email_support": "优先邮件支持",
    "landing.pricing.feat.api_access": "API 访问",
    "landing.pricing.feat.custom_rbac_roles": "自定义 RBAC 角色",
    "landing.pricing.feat.sso_google_okta": "SSO(Google、Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99.9%",
    "landing.pricing.feat.dedicated_csm": "专属 CSM",
    "landing.pricing.feat.cross_contract_ripple_analysis": "跨合同连锁分析",
    "landing.pricing.feat.approval_workflows": "审批工作流",
    "landing.pricing.feat.unlimited_everything": "一切无限",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "按租户微调的 AI",
    "landing.pricing.feat.byok_encryption": "BYOK 加密",
    "landing.pricing.feat.regional_data_residency": "区域数据驻留",
    "landing.pricing.feat.24_7_phone_support": "24/7 电话支持",
    "landing.pricing.feat.custom_compliance_packs": "定制合规包",
    "landing.pricing.feat.soc_2_type_ii_report": "SOC 2 Type II 报告",
    "landing.pricing.feat.red_team_audit": "红队审计",
    "locale.banner.detected": "我们已根据您的位置将语言设为 {lang}。",
    "locale.banner.undo": "切回英文",
    "locale.banner.keep": "保留 {lang}",
  },

  /* ============================ KOREAN ============================ */
  ko: {
    "nav.dashboard": "대시보드", "nav.studio": "계약 스튜디오", "nav.repository": "저장소",
    "nav.workflows": "워크플로우", "nav.impact": "비즈니스 임팩트", "nav.monitoring": "모니터링",
    "nav.counterparty": "거래상대방 360°", "nav.integrations": "통합",
    "nav.analytics": "분석", "nav.team": "팀 및 역할", "nav.subscription": "구독",
    "nav.compliance": "컴플라이언스", "nav.settings": "설정",
    "common.save": "저장", "common.cancel": "취소", "common.new": "새로 만들기",
    "common.ask": "Clariva에게 물어보기…", "common.signin": "로그인", "common.getstarted": "시작하기",
    "common.bookdemo": "라이브 데모 예약", "common.startfree": "무료로 시작 — 카드 불필요",
    "common.watch": "2분 둘러보기", "common.theme": "테마",
    "landing.nav.features": "기능", "landing.nav.howitworks": "작동 방식",
    "landing.nav.industries": "산업", "landing.nav.mobile": "모바일 앱",
        "landing.nav.product": "제품",
    "landing.nav.solutions": "솔루션",
    "landing.nav.testdemo": "데모 체험",
    "landing.nav.pricing": "요금제",
    "landing.nav.who": "회사 소개", "landing.nav.contact": "문의",
    "landing.hero.eyebrow": "투명한 계약 인텔리전스 플랫폼",
    "landing.hero.title": "양쪽을 모두 보세요",
    "landing.hero.subtitle": "Clariva는 작성자와 서명자에게 동등한 지능을 제공하는 최초의 AI 계약 플랫폼입니다 — 조항별 분석, 음성 브리핑, 서명 전 비즈니스 임팩트 리포트, 드래그 앤 드롭 승인 워크플로우.",
    "landing.hero.badge.soc": "SOC 2 · GDPR · HIPAA",
    "landing.hero.badge.lang": "10개 언어 · RTL",
    "landing.hero.badge.sso": "SSO · RBAC",
    "landing.hero.trustedby": "민첩한 법무·운영 팀이 신뢰하는 솔루션",
    "landing.features.eyebrow": "비법조인을 위해 만들었고, 법무팀이 사랑합니다.",
    "landing.features.title": "팀이 레드라인 PDF를 Clariva로 바꾸는 9가지 이유.",
    "landing.feat.signatory.title": "서명자 경험",
    "landing.feat.signatory.desc": "작성자가 아닌 당신의 관점에서 보는 찬반 조항 분석. 마침내 서명 시 진정한 투명성.",
    "landing.feat.audio.title": "음성 브리핑",
    "landing.feat.audio.desc": "OpenAI TTS가 60초 분량의 평이한 요약을 읽어줍니다. 계약서를 읽지 않는 임원에게 최적.",
    "landing.feat.impact.title": "비즈니스 임팩트 리포트",
    "landing.feat.impact.desc": "서명 전 8개 영역 정량 점수: 재무, 운영, 평판, 규제 외.",
    "landing.feat.workflows.title": "드래그 앤 드롭 워크플로우",
    "landing.feat.workflows.desc": "분기, 자동 트리거, 전자서명, AI 단계가 포함된 시각적 승인 디자이너.",
    "landing.feat.obligations.title": "의무 모니터링",
    "landing.feat.obligations.desc": "SLA, 마감, 위반 위험을 추적. 전체 포트폴리오에 대한 파급 분석.",
    "landing.feat.compliance.title": "컴플라이언스 보관소",
    "landing.feat.compliance.desc": "검증된 템플릿 15종: HIPAA BAA, GDPR DPA, SaaS MSA, 병원 동의서, 인플루언서, 음악 배급 등.",
    "landing.feat.ai.title": "Claude Sonnet 4.5 분석",
    "landing.feat.ai.desc": "최첨단 AI가 법률 용어를 CFO가 승인할 수 있는 결정으로 번역. 환각 없음, 인용 포함.",
    "landing.feat.global.title": "글로벌 대응",
    "landing.feat.global.desc": "10개 언어, RTL 아랍어, 다중 통화, 첫날부터 중동 + EU + APAC 데이터 거주성.",
    "landing.feat.counterparty.title": "거래상대방 360°",
    "landing.feat.counterparty.desc": "관계 그래프, 과거 레드라인, 협상 이력. 누구와 서명하는지 정확히 파악.",
    "landing.how.eyebrow": "붙여넣기에서 서명까지 4단계",
    "landing.how.title": "Clariva가 초안을 서명된 거래로 바꾸는 방법.",
    "landing.how.s1.title": "붙여넣기 또는 업로드",
    "landing.how.s1.desc": "PDF, DOCX, 일반 텍스트, Notion 링크까지 — Clariva가 모두 받아들입니다.",
    "landing.how.s2.title": "AI가 양쪽을 분석",
    "landing.how.s2.desc": "Claude가 작성자와 서명자의 양쪽 관점에서 모든 조항을 읽습니다.",
    "landing.how.s3.title": "승인을 위해 라우팅",
    "landing.how.s3.desc": "승인 체인을 드래그 앤 드롭 — 법무 → 재무 → 서명.",
    "landing.how.s4.title": "확신을 갖고 서명",
    "landing.how.s4.desc": "거래상대방은 찬반 뷰 + 음성 브리핑을 받습니다. 놀라움 없음.",
    "landing.industries.eyebrow": "지원 산업",
    "landing.industries.title": "가장 많은 문서에 서명하는 산업을 위한 전문 플레이북.",
    "landing.ind.healthcare": "헬스케어", "landing.ind.healthcare.desc": "HIPAA BAA, 수술 동의, 임상 서비스.",
    "landing.ind.clinical": "임상시험 및 제약 연구", "landing.ind.clinical.desc": "임상시험 I–IV상 연구자 계약, 시험 환자 동의 및 등록, CRO 계약, IRB 대응 프로토콜, 부작용 공개 조항.",
    "landing.ind.insurance": "보험사", "landing.ind.insurance.desc": "청약서, 약관, 특약 — 최대선의 공시 스캔 및 청구 조항 추출.",
    "landing.ind.banking": "은행 및 대출", "landing.ind.banking.desc": "개인 대출, 주택담보, 사업자 신용 — EMI 및 약정 자동 추출.",
    "landing.ind.realestate": "부동산 및 개발", "landing.ind.realestate.desc": "분양, 현금 매입, 에스크로 및 RERA 대응 계약.",
    "landing.ind.leasing": "리스 및 임대차", "landing.ind.leasing.desc": "CPI 조정 주거·상업 임대차.",
    "landing.ind.property": "부동산 관리", "landing.ind.property.desc": "소유주-관리자 계약, 신탁 회계, 공실 사이클.",
    "landing.ind.financial": "금융 서비스", "landing.ind.financial.desc": "MSA, NDA, 핀테크 파트너십, 규제 준수.",
    "landing.ind.saas": "엔터프라이즈 SaaS", "landing.ind.saas.desc": "SaaS MSA, DPA, 보안 부속서, SOC 2 인증.",
    "landing.ind.legal": "법률 및 자문", "landing.ind.legal.desc": "수임계약서, 외부 자문, 이해 상충 점검.",
    "landing.ind.education": "교육", "landing.ind.education.desc": "라이선스, 공급업체 계약, 연구 데이터 공유.",
    "landing.mobile.eyebrow": "모바일 설치",
    "landing.mobile.title.l1": "휴대폰에서 승인하세요.",
    "landing.mobile.title.l2": "오프라인 지원. 네이티브 같은 느낌.",
    "landing.mobile.desc": "Clariva는 PWA입니다 — 한 번의 탭으로 iOS나 Android 홈 화면에 추가하세요. 저장소는 오프라인 검토용으로 캐싱되며, 푸시 알림이 승인 대기 계약을 즉시 알려드립니다.",
    "landing.mobile.feat.install.title": "원탭 설치",
    "landing.mobile.feat.install.desc": "홈 화면 아이콘, 앱스토어 불필요",
    "landing.mobile.feat.offline.title": "오프라인 대응",
    "landing.mobile.feat.offline.desc": "신호 없이 읽기 모드",
    "landing.mobile.feat.push.title": "푸시 알림",
    "landing.mobile.feat.push.desc": "승인, 서명, 만료",
    "landing.mobile.qr.title": "스캔하여 열기",
    "landing.mobile.qr.subtitle": "QR 코드에 휴대폰 카메라를 비추세요",
    "landing.mobile.qr.ios": "iOS", "landing.mobile.qr.ios.hint": "Safari → 공유 → 홈 화면에 추가",
    "landing.mobile.qr.android": "Android", "landing.mobile.qr.android.hint": "Chrome → 메뉴 → 앱 설치",
    "landing.mobile.qr.fallback": "또는 바로 방문",
    "landing.testimonials.eyebrow": "사용자 후기",
    "landing.testimonials.title": "「우리는 패럴리걸 두 명과 전자서명 도구를 대체했습니다.」",
    "landing.who.eyebrow": "회사 소개",
    "landing.who.title": "계약에 집착하는 팀, 전직 리걸테크 창업자들과 AI 엔지니어들.",
    "landing.who.desc": "Clariva는 두바이에서 한 가지 신념으로 시작되었습니다: 서명자는 작성자와 동일한 지능을 받을 자격이 있다. 시리즈 D 핀테크에서 법무를 이끌 때 갖고 싶었던 플랫폼을 만들고 있습니다.",
    "landing.contact.eyebrow": "이야기해 봐요",
    "landing.contact.title": "30분 라이브 데모 예약.",
    "landing.contact.desc": "가장 까다로운 계약을 보여주세요. Clariva에서 라이브로 돌려드립니다. 슬라이드도 녹화도 없이, 오로지 제품만.",
    "landing.contact.form.name": "성명", "landing.contact.form.email": "업무 이메일",
    "landing.contact.form.company": "회사", "landing.contact.form.message": "어떤 부분을 보고 싶으세요?",
    "landing.contact.form.submit": "데모 요청",
    "landing.final.title": "다음 계약을 양쪽 시각으로 보세요.",
    "landing.final.desc": "무료 워크스페이스, 샘플 계약 3건, 신용카드 불필요.",
    "landing.footer.tagline": "투명한 계약 인텔리전스 플랫폼.",
    "landing.footer.product": "제품", "landing.footer.company": "회사",
    "landing.footer.legal": "법적 고지", "landing.footer.rights": "모든 권리 보유.",
    "landing.pricing.eyebrow": "모든 팀을 위한 요금제",
    "landing.pricing.title": "단순하고 투명한 가격.",
    "landing.pricing.subtitle": "9개 모듈 모두 무료로 시작. 한도를 넘으면 업그레이드하세요.",
    "landing.pricing.popular": "가장 인기",
    "landing.pricing.free": "무료", "landing.pricing.starter": "스타터",
    "landing.pricing.pro": "프로페셔널", "landing.pricing.enterprise": "엔터프라이즈",
    "landing.pricing.cta.free": "무료로 시작",
    "landing.pricing.cta.paid": "요금제 선택",
    "landing.pricing.cta.enterprise": "영업팀에 문의",
    "landing.pricing.contact": "맞춤형",
    "landing.pricing.billed.monthly": "사용자당 / 월",
    "landing.pricing.billed.forever": "영구 무료, 카드 불필요",
    "landing.pricing.billed.contact": "팀에 맞춤 제공",
    "landing.pricing.unlimited": "무제한",
    "landing.pricing.contracts": "계약",
    "landing.pricing.seats": "팀 좌석",
    "landing.pricing.fineprint": "가격은 {currency}로 표시. USD에서 참고 환율로 환산 — 계약 통화로 청구됩니다.",
    
    "landing.process.eyebrow": "Clariva 프로세스",
    "landing.process.title.l1": "초안에서 서명까지,",
    "landing.process.title.l2": "여섯 단계의 투명한 여정.",
    "landing.process.subtitle": "모든 단계가 계측됩니다. 모든 AI 결정이 설명됩니다. 모든 서명자가 당신과 같은 화면을 봅니다.",
    "landing.process.s1.title": "업로드 또는 작성",
    "landing.process.s1.desc": "PDF, DOCX 또는 15가지 산업 템플릿 중 하나로 시작. OCR 지원.",
    "landing.process.s2.title": "AI가 모든 조항을 해부",
    "landing.process.s2.desc": "Claude Sonnet 4.5가 각 조항의 위험을 평가하고 의무에 매핑하며 법률 용어를 번역합니다.",
    "landing.process.s3.title": "비즈니스 임팩트 리포트",
    "landing.process.s3.desc": "8개 영역 점수 카드: 재무, 운영, 평판 등 — 몇 초 만에.",
    "landing.process.s4.title": "음성 브리핑",
    "landing.process.s4.desc": "OpenAI TTS 기반 60초 음성 요약. 임원과 거래상대방에게 공유 가능한 링크.",
    "landing.process.s5.title": "워크플로우로 라우팅",
    "landing.process.s5.desc": "드래그 앤 드롭 디자이너: 승인, 결정 분기, 병렬 검토, 자동 트리거.",
    "landing.process.s6.title": "감사 추적과 함께 서명",
    "landing.process.s6.desc": "QR 공유 가능 전자서명, 변조 불가 로그, 서명 즉시 의무 모니터링 가동.",
    "landing.walkthrough.eyebrow": "2분 둘러보기",
    "landing.walkthrough.title": "Clariva가 실제 MSA를 해부하는 모습을 보세요.",
    "landing.walkthrough.subtitle": "투입부터 서명까지 완전한 계약이 2분 안에 처리되는 과정 — 모든 AI 결정 해설 포함.",
    "landing.walkthrough.duration": "2:14 · 보이스오버 포함",
    "landing.walkthrough.kpi1.n": "2.1×",
    "landing.walkthrough.kpi1.l": "더 빠른 협상 사이클",
    "landing.walkthrough.kpi2.n": "68%",
    "landing.walkthrough.kpi2.l": "놓친 의무 감소",
    "landing.walkthrough.kpi3.l": "1년 차 평균 절감",
    "landing.testimonials.t1.quote": "법무 책임자가 주 40건을 읽던 일에서 각 10분 만에 Clariva 임팩트 리포트를 검토하는 일로 바뀌었습니다. 지난 분기 3배 더 많은 거래를 마감했어요.",
    "landing.testimonials.t1.name": "마야 첸",
    "landing.testimonials.t1.role": "COO, CloudMosaic Technologies",
    "landing.testimonials.t2.quote": "음성 브리핑만으로도 플랫폼 비용이 회수됩니다. 우리 CEO는 출퇴근길에 듣습니다.",
    "landing.testimonials.t2.name": "루이스 리베라",
    "landing.testimonials.t2.role": "VP Legal Ops, Harmonic Wave Records",
    "landing.testimonials.t3.quote": "드디어 서명자를 부수적으로 다루지 않는 도구. 서명 전에 투명한 위험 분석을 받기 때문에 거래처들이 더 신뢰합니다.",
    "landing.testimonials.t3.name": "프리야 라흐만",
    "landing.testimonials.t3.role": "창립자, Arcadia Health Collective",
    "landing.who.title2": "법무 운영자, ML 연구자, 디자이너로 구성된 팀 — 한 가지 신념으로 뭉쳤습니다.",
    "landing.who.desc2": "계약은 한쪽에 치우친 무기여서는 안 됩니다. 두 당사자가 같은 명확성을 가진 공유된 이해여야 합니다. Clariva는 고객이 이해하지 못한 채 무언가에 서명하는 모습을 지켜보다 지친 법률 사무소 안에서 태어났습니다. 그것을 과거로 만들기 위해 존재합니다.",
    "landing.who.pillar1.title": "투명성이 우선",
    "landing.who.pillar1.desc": "AI 추론을 숨기지 않습니다. 모든 점수와 위험 표시는 원본 문서 인용과 함께 제공됩니다.",
    "landing.who.pillar2.title": "서명자가 중요합니다",
    "landing.who.pillar2.desc": "계약은 양측을 구속합니다. 처음에 작성하는 변호사뿐 아니라 마지막에 서명하는 사람을 위해 만듭니다.",
    "landing.who.pillar3.title": "엔터프라이즈급 속도",
    "landing.who.pillar3.desc": "1초 미만 조항 분석, 스트리밍 오디오, 무중단 배포. Claude 4.5 + FastAPI + React 위에 구축.",
    "landing.contact.eyebrow2": "이야기해 봐요",
    "landing.contact.title2": "전체 데모를 예약하거나 eSession을 잡으세요.",
    "landing.contact.desc2": "팀에 대해 조금 알려 주세요 — 해당 산업 경험이 있는 전문가와 연결하고, 실제 계약(NDA 하에)으로 맞춤 둘러보기를 준비해 드립니다.",
    "landing.contact.bullet1": "30분 맞춤 제품 둘러보기",
    "landing.contact.bullet2": "실제 계약에 대한 라이브 조항 분석",
    "landing.contact.bullet3": "구현 및 출시 플레이북",
    "landing.contact.bullet4": "법무 지출에 대한 맞춤 ROI 모델",
    "landing.contact.intent.demo": "전체 데모",
    "landing.contact.intent.esession": "eSession 잡기",
    "landing.contact.intent.sales": "영업팀에 연락",
    "landing.contact.placeholder.name": "김 변호사",
    "landing.contact.placeholder.email": "kim@company.com",
    "landing.contact.placeholder.company": "회사명",
    "landing.contact.placeholder.message": "법무 운영팀을 위한 계약 인텔리전스를 평가 중입니다...",
    "landing.contact.field.preferred_date": "원하는 날짜",
    "landing.contact.field.preferred_time": "원하는 시간",
    "landing.contact.field.message": "무엇을 이루고 싶으세요?",
    "landing.contact.privacy": "영업시간 내 2시간 안에 답변. 스팸은 절대 없습니다.",
    "landing.contact.submit.demo": "전체 데모 요청",
    "landing.contact.submit.esession": "eSession 예약",
    "landing.contact.submit.sales": "영업팀에 연락",
    "landing.contact.submitting": "전송 중…",
    "landing.contact.success.title": "접수되었습니다.",
    "landing.contact.success.desc": "영업시간 내 2시간 안에 일정 링크를 보내드릴게요.",
    "landing.contact.toast.required": "이름과 이메일은 필수입니다",
    "landing.contact.toast.success": "감사합니다! 곧 연락드릴게요.",
    "landing.contact.toast.esession": "eSession 요청을 접수했습니다 — 영업시간 내 2시간 안에 확인됩니다",
    "landing.final.title2.l1": "다음 계약을",
    "landing.final.title2.l2": "양쪽 시각에서 보세요.",
    "landing.final.desc2": "무료로 시작. 카드 불필요. 가입 30초 미만, 풀 데모 워크스페이스 포함.",
    "landing.final.cta.start": "지금 무료로 시작",
    "landing.final.cta.demo": "demo@clariva.ai 체험",
    "landing.footer.tagline2": "투명한 AI 계약 인텔리전스. 작성자와 서명자에게 동등하게.",
    "landing.footer.product.items": "기능,워크플로우,음성 브리핑,비즈니스 임팩트",
    "landing.footer.company.items": "회사 소개,채용,프레스 키트,보안",
    "landing.footer.legal.items": "이용약관,개인정보,DPA,SOC 2 리포트",
    "landing.footer.copyright": "© 2026 Clariva. 투명한 AI 계약 인텔리전스.",
    "landing.footer.madein": "SF · 런던 · 두바이에서 정밀하게 제작",
        "landing.pricing.feat.all_9_modules": "9개 모듈 전체",
    "landing.pricing.feat.ai_contract_analysis": "AI 계약 분석",
    "landing.pricing.feat.business_impact_reports": "비즈니스 임팩트 리포트",
    "landing.pricing.feat.audio_briefings_limited": "음성 브리핑 (제한)",
    "landing.pricing.feat.email_support": "이메일 지원",
    "landing.pricing.feat.everything_in_free": "무료 플랜의 모든 것",
    "landing.pricing.feat.everything_in_starter": "스타터 플랜의 모든 것",
    "landing.pricing.feat.everything_in_professional": "프로페셔널 플랜의 모든 것",
    "landing.pricing.feat.5_team_seats": "5 팀 좌석",
    "landing.pricing.feat.25_team_seats": "25 팀 좌석",
    "landing.pricing.feat.10_integrations": "10 통합",
    "landing.pricing.feat.custom_templates": "맞춤 템플릿",
    "landing.pricing.feat.priority_email_support": "우선 이메일 지원",
    "landing.pricing.feat.api_access": "API 액세스",
    "landing.pricing.feat.custom_rbac_roles": "맞춤 RBAC 역할",
    "landing.pricing.feat.sso_google_okta": "SSO (Google, Okta)",
    "landing.pricing.feat.sla_99_9": "SLA 99.9%",
    "landing.pricing.feat.dedicated_csm": "전담 CSM",
    "landing.pricing.feat.cross_contract_ripple_analysis": "계약 간 파급 분석",
    "landing.pricing.feat.approval_workflows": "승인 워크플로우",
    "landing.pricing.feat.unlimited_everything": "모든 것 무제한",
    "landing.pricing.feat.saml_sso_ad_ldap": "SAML SSO + AD/LDAP",
    "landing.pricing.feat.per_tenant_fine_tuned_ai": "테넌트별 파인튜닝 AI",
    "landing.pricing.feat.byok_encryption": "BYOK 암호화",
    "landing.pricing.feat.regional_data_residency": "지역별 데이터 거주",
    "landing.pricing.feat.24_7_phone_support": "24/7 전화 지원",
    "landing.pricing.feat.custom_compliance_packs": "맞춤 컴플라이언스 팩",
    "landing.pricing.feat.soc_2_type_ii_report": "SOC 2 Type II 리포트",
    "landing.pricing.feat.red_team_audit": "레드팀 감사",
    "locale.banner.detected": "위치를 기반으로 언어를 {lang}(으)로 설정했습니다.",
    "locale.banner.undo": "영어로 되돌리기",
    "locale.banner.keep": "{lang} 유지",
  },
};

export default T;
